// src/App.js
import React, { useState, useMemo, useEffect, useLayoutEffect, Fragment, useRef } from 'react';
import './App.css';

function CombinedEditor({ rowIndex, item, onCommit }) {
  const [idVal, setIdVal]   = useState(item.id != null ? String(item.id) : '');
  const [petVal, setPetVal] = useState(item.pet_species_id != null ? String(item.pet_species_id) : '');
  const wrapperRef = useRef(null);

  const handleBlur = e => {
    if (!wrapperRef.current.contains(e.relatedTarget)) {
      onCommit(rowIndex, idVal.trim(), petVal.trim());
    }
  };
  const onKeyDown = e => {
    if (e.key === 'Enter') onCommit(rowIndex, idVal.trim(), petVal.trim());
  };

  return (
    <span ref={wrapperRef} onBlur={handleBlur} className="combined-editor">
      <input
        type="number"
        inputMode="numeric"
        value={idVal}
        placeholder="ID"
        onChange={e => setIdVal(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={petVal.trim() !== ''}
        autoFocus
      />
      <span>/</span>
      <input
        type="number"
        inputMode="numeric"
        value={petVal}
        placeholder="Pet ID"
        onChange={e => setPetVal(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={idVal.trim() !== ''}
      />
    </span>
  );
}

const API_BASE = process.env.NODE_ENV === 'production'
  ? '/api'
  : 'http://localhost:8080';
const EMPTY_ITEM = {
  name: '',
  id: '',
  pet_species_id: '',
  ilvl: '',
  desired_price: '',
  socket: false,
  extra_stat: '',
  add_other_region: false
};
const EMPTY_ILVL_VARIATION = {
  visible: false,
  items: [],
  ilvls: '',
  desired_price_mode: 'one_gold',
  add_other_region: false,
  submitting: false
};
const EMPTY_VARIANT_MODAL = {
  visible: false,
  items: [],
  socket: false,
  extra_stat: '',
  automatedprice: '',
  automatedprice_sign: 'minus'
};
const EMPTY_AUTO_MODAL = {
  visible: false,
  rowIndex: null,
  items: [],
  pct: '',
  sign: 'minus',
  submitting: false
};
const VARIANT_GROUP_PREFIX = 'variant-group:';
const VARIANT_GROUP_LABELS = {
  any_secondary: 'Any secondary (no Indestructible)',
  socket_any_secondary: 'Socket + Any secondary (no Indestructible)',
  any_secondary_or_socket: 'Any secondary or socket'
};
const getKey = item =>
  item.id != null && item.id !== '' ? item.id : item.pet_species_id ?? item.name ?? '';
const getVariantGroupKey = item => item.variant_group
  ? `${VARIANT_GROUP_PREFIX}${getKey(item)}|${item.ilvl ?? 0}|${item.variant_group}`
  : null;
const isVariantGroupKey = key =>
  typeof key === 'string' && key.startsWith(VARIANT_GROUP_PREFIX);
const EXTRA_STATS = [
  { value: '', label: 'None' },
  { value: 'any', label: 'Any secondary (Speed/Avoidance/Leech)' },
  { value: 'any_or_socket', label: 'Any secondary or socket' },
  { value: 'speed', label: 'Speed' },
  { value: 'avoidance', label: 'Avoidance' },
  { value: 'leech', label: 'Leech' },
  { value: 'indestructible', label: 'Indestructible' }
];
const PRICING_FORMULAS = [
  {
    value: 'value_ladder',
    label: 'Value ladder',
    detail: '1m+ -30%, 300k+ -45%, 100k+ -55%, lower -65%'
  },
  {
    value: 'profit_band',
    label: 'Profit band',
    detail: 'uses your target as mid tier; high 1.33x, low 0.67x'
  },
  {
    value: 'high_value_margin',
    label: 'High-value margin',
    detail: 'uses your target as base; 300k+ 1.8x, 1m+ 3x, 2m+ 5x'
  },
  {
    value: 'conservative',
    label: 'Conservative',
    detail: '1m+ -25%, 300k+ -35%, 100k+ -45%, lower -55%'
  },
  {
    value: 'aggressive',
    label: 'Aggressive',
    detail: '1m+ -40%, 300k+ -55%, 100k+ -65%, lower -75%'
  },
  {
    value: 'fixed_profit',
    label: 'Fixed profit',
    detail: 'N Avg minus your target gold'
  }
];
const AH_SALE_CUT_RATE = 0.95;
const ESTIMATED_RESALE_RATE = 0.98;
const PROFIT_TARGET_FORMULAS = new Set(['profit_band', 'high_value_margin', 'fixed_profit']);

const PACIFIC_TIME_ZONE = 'America/Los_Angeles';
const SCHEDULE_TIME_MODES = [
  { value: 'pacific', label: 'Pacific' },
  { value: 'local', label: 'My time' },
  { value: 'both', label: 'Both' }
];
const scheduleTimeModeKey = 'scheduleTimeMode';

function readScheduleTimeMode() {
  try {
    const saved = localStorage.getItem(scheduleTimeModeKey);
    return SCHEDULE_TIME_MODES.some(mode => mode.value === saved) ? saved : 'pacific';
  } catch (err) {
    return 'pacific';
  }
}

function getLocalTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (err) {
    return '';
  }
}

function zonedParts(timestampMs, timeZone) {
  const parts = {};
  new Intl.DateTimeFormat('en-US', {
    timeZone: timeZone || undefined,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZoneName: 'short'
  }).formatToParts(new Date(timestampMs)).forEach(part => {
    parts[part.type] = part.value;
  });
  return parts;
}

// Same shape as the backend's Pacific display: 2026-10-21 23:45 PDT.
function formatZonedTimestamp(timestampSeconds, timeZone) {
  const parts = zonedParts(Number(timestampSeconds) * 1000, timeZone);
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute} ${parts.timeZoneName}`;
}

function formatScheduleTimestamp(timestampSeconds, pacificDisplay, mode) {
  if (!timestampSeconds) return pacificDisplay || 'not planned';
  const pacific = pacificDisplay || formatZonedTimestamp(timestampSeconds, PACIFIC_TIME_ZONE);
  if (mode === 'pacific') return pacific;
  const local = formatZonedTimestamp(timestampSeconds, getLocalTimeZone());
  return mode === 'local' ? local : `${pacific} · ${local}`;
}

// Converts a Pacific HH:MM (as entered in the schedule) to the viewer's clock for today's date.
function pacificClockToLocal(clock) {
  const match = /^(\d{2}):(\d{2})$/.exec(clock || '');
  if (!match) return '';
  const now = Date.now();
  const today = zonedParts(now, PACIFIC_TIME_ZONE);
  const wallAsUtc = Date.UTC(Number(today.year), Number(today.month) - 1, Number(today.day),
    Number(match[1]), Number(match[2]));
  // Shift by the Pacific UTC offset in effect at that wall time (twice settles DST edges).
  let instant = wallAsUtc;
  for (let i = 0; i < 2; i += 1) {
    const seen = zonedParts(instant, PACIFIC_TIME_ZONE);
    const seenAsUtc = Date.UTC(Number(seen.year), Number(seen.month) - 1, Number(seen.day),
      Number(seen.hour), Number(seen.minute));
    instant += wallAsUtc - seenAsUtc;
  }
  const local = zonedParts(instant, getLocalTimeZone());
  return `${local.hour}:${local.minute} ${local.timeZoneName}`;
}

function clockMinutes(clock) {
  const match = /^(\d{2}):(\d{2})$/.exec(clock || '');
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
}

function formatMinutesDuration(totalMinutes) {
  const minutes = Math.max(0, Math.round(totalMinutes));
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${rest}m`;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
}

// Run and sleep lengths of the base window; a stop at or before the start is the next day.
function scheduleDurations(schedule) {
  const start = clockMinutes(schedule.start_time);
  const stop = clockMinutes(schedule.stop_time);
  if (start == null || stop == null) return null;
  const run = (((stop - start) % 1440) + 1440) % 1440;
  if (!run) return { invalid: true };
  const randomMax = Number(schedule.random_max_minutes);
  const spread = Number.isFinite(randomMax) && randomMax > 0 ? 2 * randomMax : 0;
  return {
    run,
    sleep: 1440 - run,
    spread,
    overnight: stop <= start
  };
}

function durationRange(base, spread) {
  if (!spread) return formatMinutesDuration(base);
  return `${formatMinutesDuration(base)} (${formatMinutesDuration(base - spread)} to ${formatMinutesDuration(base + spread)})`;
}

async function getSchedulePayload() {
  const response = await fetch(`${API_BASE}/control/schedules`);
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.error || `Could not load schedules (HTTP ${response.status})`);
  }
  return payload;
}

function StopTimerControls({ title, timer, values, busy, onChange, onSet, onCancel }) {
  return (
    <div className={timer?.active ? 'stop-timer active' : 'stop-timer'}>
      <div className="stop-timer-heading">
        <strong>{title}</strong>
        <span>
          {timer?.active
            ? `Scheduled for ${timer.stop_display}`
            : 'No one-time stop scheduled'}
        </span>
      </div>
      <div className="stop-timer-controls">
        <label>
          Hours
          <input
            type="number"
            min="0"
            max="168"
            step="1"
            value={values.hours}
            onChange={event => onChange('hours', event.target.value)}
          />
        </label>
        <label>
          Minutes
          <input
            type="number"
            min="0"
            max="59"
            step="1"
            value={values.minutes}
            onChange={event => onChange('minutes', event.target.value)}
          />
        </label>
        <button type="button" onClick={onSet} disabled={busy}>
          {busy ? 'Saving...' : timer?.active ? 'Replace timer' : 'Set timer'}
        </button>
        {timer?.active && (
          <button type="button" className="cancel-stop-timer" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

function SchedulesPanel() {
  const [agents, setAgents] = useState([]);
  const [timeMode, setTimeMode] = useState(readScheduleTimeMode);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [timerSaving, setTimerSaving] = useState({});
  const [stopAfter, setStopAfter] = useState({});
  const [status, setStatus] = useState('');

  const applyPayload = payload => {
    setAgents(Array.isArray(payload.agents) ? payload.agents : []);
  };

  const refresh = async () => {
    setStatus('');
    try {
      applyPayload(await getSchedulePayload());
    } catch (err) {
      setStatus(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    getSchedulePayload()
      .then(payload => {
        if (active) applyPayload(payload);
      })
      .catch(err => {
        if (active) setStatus(err.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, []);

  const changeTimeMode = mode => {
    setTimeMode(mode);
    try {
      localStorage.setItem(scheduleTimeModeKey, mode);
    } catch (err) {
      // Remembering the choice is optional.
    }
  };

  const updateField = (agentId, field, value) => {
    setAgents(current => current.map(agent => (
      agent.agent_id === agentId
        ? { ...agent, schedule: { ...agent.schedule, [field]: value } }
        : agent
    )));
  };

  const getStopAfter = target => stopAfter[target] || { hours: '5', minutes: '0' };

  const updateStopAfter = (target, field, value) => {
    setStopAfter(current => ({
      ...current,
      [target]: {
        ...(current[target] || { hours: '5', minutes: '0' }),
        [field]: value
      }
    }));
  };

  const setStopTimer = async (target, label) => {
    const values = getStopAfter(target);
    if (!/^\d+$/.test(values.hours) || !/^\d+$/.test(values.minutes)) {
      setStatus('Stop-after hours and minutes must be whole non-negative numbers.');
      return;
    }
    const hours = Number(values.hours);
    const minutes = Number(values.minutes);
    const durationMinutes = hours * 60 + minutes;
    if (minutes > 59 || durationMinutes < 1 || durationMinutes > 7 * 24 * 60) {
      setStatus('Stop-after duration must be between 1 minute and 7 days, with minutes from 0 to 59.');
      return;
    }

    setTimerSaving(current => ({ ...current, [target]: true }));
    setStatus('');
    try {
      const response = await fetch(`${API_BASE}/control/stop-timers/${target}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ duration_minutes: durationMinutes })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || `Could not set stop timer (HTTP ${response.status})`);
      }
      applyPayload(await getSchedulePayload());
      setStatus(`${label} will stop in ${hours ? `${hours}h ` : ''}${minutes ? `${minutes}m` : ''}`.trim());
    } catch (err) {
      setStatus(err.message);
    } finally {
      setTimerSaving(current => ({ ...current, [target]: false }));
    }
  };

  const cancelStopTimer = async (target, label) => {
    setTimerSaving(current => ({ ...current, [target]: true }));
    setStatus('');
    try {
      const response = await fetch(`${API_BASE}/control/stop-timers/${target}`, {
        method: 'DELETE'
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || `Could not cancel stop timer (HTTP ${response.status})`);
      }
      applyPayload(await getSchedulePayload());
      setStatus(`${label} one-time stop cancelled.`);
    } catch (err) {
      setStatus(err.message);
    } finally {
      setTimerSaving(current => ({ ...current, [target]: false }));
    }
  };

  const saveSchedule = async (agent, enabledOverride = null) => {
    const enabled = enabledOverride == null ? agent.schedule.enabled : enabledOverride;
    const body = {
      enabled,
      start_time: agent.schedule.start_time,
      stop_time: agent.schedule.stop_time,
      random_min_minutes: Number(agent.schedule.random_min_minutes),
      random_max_minutes: Number(agent.schedule.random_max_minutes)
    };
    setSaving(current => ({ ...current, [agent.agent_id]: true }));
    setStatus('');
    try {
      const response = await fetch(`${API_BASE}/control/schedules/${agent.agent_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || `Could not save schedule (HTTP ${response.status})`);
      }
      const refreshed = await getSchedulePayload();
      applyPayload(refreshed);
      setStatus(`${agent.label} schedule ${enabled ? 'saved and enabled' : 'disabled'}.`);
    } catch (err) {
      setStatus(err.message);
    } finally {
      setSaving(current => ({ ...current, [agent.agent_id]: false }));
    }
  };

  const offsetLabel = offset => `${Number(offset) >= 0 ? '+' : ''}${offset} min`;
  const activeStopTimers = agents.filter(agent => agent.stop_timer?.active);
  const commonStopAt = activeStopTimers.length === agents.length && agents.length > 0 &&
    activeStopTimers.every(agent => agent.stop_timer.stop_at === activeStopTimers[0].stop_timer.stop_at)
    ? formatScheduleTimestamp(
      activeStopTimers[0].stop_timer.stop_at, activeStopTimers[0].stop_timer.stop_display, timeMode
    )
    : 'individual deadlines';
  const showLocal = timeMode !== 'pacific';

  return (
    <section className="schedules-panel">
      <div className="schedules-heading">
        <div>
          <h1>Laptop schedules</h1>
          <p>
            Base start and stop are entered in Pacific time (PST/PDT). A stop at or before the
            start means the next day. Each daily start and stop moves independently earlier or
            later by a random amount in the selected range.
          </p>
        </div>
        <div className="schedules-heading-actions">
          <div className="schedule-time-mode" role="group" aria-label="Show times in">
            {SCHEDULE_TIME_MODES.map(mode => (
              <button
                type="button"
                key={mode.value}
                className={timeMode === mode.value ? 'active' : ''}
                aria-pressed={timeMode === mode.value}
                onClick={() => changeTimeMode(mode.value)}
              >
                {mode.label}
              </button>
            ))}
          </div>
          <button type="button" onClick={refresh} disabled={loading}>Refresh</button>
        </div>
      </div>

      {status && <div className="schedule-message">{status}</div>}
      {loading ? (
        <p>Loading schedules...</p>
      ) : (
        <>
          <StopTimerControls
            title="Stop all laptops after"
            timer={{
              active: activeStopTimers.length > 0,
              stop_display: commonStopAt
            }}
            values={getStopAfter('all')}
            busy={Boolean(timerSaving.all)}
            onChange={(field, value) => updateStopAfter('all', field, value)}
            onSet={() => setStopTimer('all', 'All laptops')}
            onCancel={() => cancelStopTimer('all', 'All laptops')}
          />
          <div className="schedule-grid">
            {agents.map(agent => {
            const schedule = agent.schedule;
            const busy = Boolean(saving[agent.agent_id]);
            const timerBusy = Boolean(timerSaving[agent.agent_id]);
            const durations = scheduleDurations(schedule);
            const planRun = schedule.start_at && schedule.stop_at
              ? (Number(schedule.stop_at) - Number(schedule.start_at)) / 60
              : null;
            return (
              <article className={`schedule-card ${schedule.enabled ? 'enabled' : ''}`} key={agent.agent_id}>
                <div className="schedule-card-heading">
                  <div>
                    <h2>{agent.label}</h2>
                    <span>{agent.alias} · {agent.online ? 'online' : 'offline'} · {agent.reported_status}</span>
                  </div>
                  <span className={`schedule-state ${schedule.enabled ? 'enabled' : ''}`}>
                    {schedule.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>

                <div className="schedule-fields">
                  <label>
                    Base start
                    <input
                      type="time"
                      value={schedule.start_time}
                      onChange={event => updateField(agent.agent_id, 'start_time', event.target.value)}
                    />
                    {showLocal && schedule.start_time && (
                      <small>Your time: {pacificClockToLocal(schedule.start_time)}</small>
                    )}
                  </label>
                  <label>
                    Base stop
                    <input
                      type="time"
                      value={schedule.stop_time}
                      onChange={event => updateField(agent.agent_id, 'stop_time', event.target.value)}
                    />
                    {showLocal && schedule.stop_time && (
                      <small>Your time: {pacificClockToLocal(schedule.stop_time)}</small>
                    )}
                  </label>
                  <label>
                    Random minimum (minutes)
                    <input
                      type="number"
                      min="0"
                      max="360"
                      step="1"
                      value={schedule.random_min_minutes}
                      onChange={event => updateField(agent.agent_id, 'random_min_minutes', event.target.value)}
                    />
                  </label>
                  <label>
                    Random maximum (minutes)
                    <input
                      type="number"
                      min="0"
                      max="360"
                      step="1"
                      value={schedule.random_max_minutes}
                      onChange={event => updateField(agent.agent_id, 'random_max_minutes', event.target.value)}
                    />
                  </label>
                </div>

                {durations && (
                  <div className={durations.invalid ? 'schedule-durations invalid' : 'schedule-durations'}>
                    {durations.invalid ? (
                      <span>Start and stop must be different times.</span>
                    ) : (
                      <>
                        <span>
                          Runs <strong>{durationRange(durations.run, durations.spread)}</strong>
                          {durations.overnight ? ', overnight' : ''}
                        </span>
                        <span>
                          Sleeps <strong>{durationRange(durations.sleep, durations.spread)}</strong> before the next start
                        </span>
                      </>
                    )}
                  </div>
                )}

                {schedule.enabled ? (
                  <div className="schedule-plan">
                    <strong>Plan for {schedule.plan_date}</strong>
                    <span>
                      Start: {formatScheduleTimestamp(schedule.start_at, schedule.start_display, timeMode)}{' '}
                      ({offsetLabel(schedule.start_offset_minutes)}) ·{' '}
                      {schedule.start_fired ? 'sent' : 'pending'}
                    </span>
                    <span>
                      Stop: {formatScheduleTimestamp(schedule.stop_at, schedule.stop_display, timeMode)}{' '}
                      ({offsetLabel(schedule.stop_offset_minutes)}) ·{' '}
                      {schedule.stop_fired ? 'sent' : 'pending'}
                    </span>
                    {planRun != null && planRun > 0 && (
                      <span>This run: {formatMinutesDuration(planRun)}</span>
                    )}
                  </div>
                ) : (
                  <p className="schedule-plan disabled">
                    Enabling generates and saves today&apos;s randomized start and stop.
                  </p>
                )}

                <StopTimerControls
                  title="One-time stop after"
                  timer={agent.stop_timer?.active ? {
                    ...agent.stop_timer,
                    stop_display: formatScheduleTimestamp(
                      agent.stop_timer.stop_at, agent.stop_timer.stop_display, timeMode
                    )
                  } : agent.stop_timer}
                  values={getStopAfter(agent.agent_id)}
                  busy={timerBusy}
                  onChange={(field, value) => updateStopAfter(agent.agent_id, field, value)}
                  onSet={() => setStopTimer(agent.agent_id, agent.label)}
                  onCancel={() => cancelStopTimer(agent.agent_id, agent.label)}
                />

                <div className="schedule-actions">
                  <button type="button" onClick={() => saveSchedule(agent)} disabled={busy}>
                    {busy ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    className={schedule.enabled ? 'disable' : 'enable'}
                    onClick={() => saveSchedule(agent, !schedule.enabled)}
                    disabled={busy}
                  >
                    {schedule.enabled ? 'Turn off' : 'Turn on'}
                  </button>
                </div>
              </article>
            );
            })}
          </div>
        </>
      )}
      <p className="schedule-footnote">
        A daily or one-time stop shuts down the supervisor, scanner processes, and WoW windows. The
        laptop and remote agent stay online. One-time timers do not change the daily schedule.
      </p>
    </section>
  );
}

async function getPointBlankPayload() {
  const response = await fetch(`${API_BASE}/control/files`);
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.error || `Could not load file versions (HTTP ${response.status})`);
  }
  return payload;
}

async function postPointBlankJson(path, body) {
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(result.error || `Request failed (HTTP ${response.status})`);
  }
  return result;
}

const IN_PROGRESS_DEPLOY_PHASES = ['stopping', 'updating', 'resuming'];
const DEPLOY_PHASE_LABELS = {
  pending: 'Pending (waiting for laptop)',
  stopping: 'Stopping WoW',
  updating: 'Installing file',
  resuming: 'Restoring run state',
  completed: 'Completed',
  failed: 'Failed',
  aborted: 'Aborted'
};

// Keep the operator's ticks across refreshes; the first load ticks every enabled laptop.
const nextCheckedAgents = (current, payload) => {
  const enabledIds = (payload.agents || [])
    .filter(agent => agent.file_updates_enabled)
    .map(agent => agent.agent_id);
  return current === null ? enabledIds : current.filter(id => enabledIds.includes(id));
};

const formatElapsed = seconds => {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h ${minutes % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
};

function PointBlankPanel() {
  const [payload, setPayload] = useState(null);
  const [checked, setChecked] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const fileInputRef = useRef(null);

  const refresh = async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const next = await getPointBlankPayload();
      setPayload(next);
      setChecked(current => nextCheckedAgents(current, next));
    } catch (err) {
      setMessage(err.message);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const next = await getPointBlankPayload();
        if (mounted) {
          setPayload(next);
          setChecked(current => nextCheckedAgents(current, next));
        }
      } catch (err) {
        if (mounted) setMessage(err.message);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    const timer = window.setInterval(load, 5000);
    return () => {
      mounted = false;
      window.clearInterval(timer);
    };
  }, []);

  const agents = payload?.agents || [];
  const checkedAgents = agents.filter(agent => (checked || []).includes(agent.agent_id));
  const lockedChecked = checkedAgents.filter(agent => agent.locked);
  const abortableAgents = agents.filter(agent => agent.abortable);
  const canDeploy = !busy && checkedAgents.length > 0 && lockedChecked.length === 0;
  const checkedNames = checkedAgents.map(agent => agent.label).join(', ');

  const toggleAgent = agentId => {
    setChecked(current => {
      const ids = current || [];
      return ids.includes(agentId) ? ids.filter(id => id !== agentId) : [...ids, agentId];
    });
  };

  const upload = async () => {
    if (!selectedFile) {
      setMessage('Choose PointBlankSniper.lua first.');
      return;
    }
    if (selectedFile.name !== 'PointBlankSniper.lua') {
      setMessage('The file must be named exactly PointBlankSniper.lua.');
      return;
    }
    if (checkedAgents.length === 0) {
      setMessage('Tick at least one laptop to deploy to.');
      return;
    }
    const body = new FormData();
    body.append('targets', checkedAgents.map(agent => agent.agent_id).join(','));
    body.append('file', selectedFile);
    setBusy(true);
    setMessage(`Uploading and deploying to ${checkedNames}...`);
    try {
      const response = await fetch(`${API_BASE}/control/files/upload`, { method: 'POST', body });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.error || `Upload failed (HTTP ${response.status})`);
      }
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setMessage(
        `Version ${result.version.version_id} uploaded and deploying to ${checkedNames}. ` +
        'Offline laptops will update when they come back online.'
      );
      await refresh();
    } catch (err) {
      setMessage(err.message);
    } finally {
      setBusy(false);
    }
  };

  const deployVersion = async version => {
    const confirmed = window.confirm(
      `Deploy ${version.version_id} to ${checkedNames}? WoW on those laptops will be closed first; ` +
      'offline laptops update when they come back online.'
    );
    if (!confirmed) return;
    setBusy(true);
    setMessage(`Starting deployment of ${version.version_id}...`);
    try {
      await postPointBlankJson('/control/files/deploy', {
        version_id: version.version_id,
        targets: checkedAgents.map(agent => agent.agent_id)
      });
      setMessage(`Deployment of ${version.version_id} started for ${checkedNames}.`);
      await refresh();
    } catch (err) {
      setMessage(err.message);
    } finally {
      setBusy(false);
    }
  };

  const abortDeployment = async agent => {
    const label = agent ? agent.label : 'every laptop';
    const confirmed = window.confirm(
      `Abort the PointBlankSniper deployment on ${label}? Laptops that were stopped for it ` +
      'go back to their previous run state.'
    );
    if (!confirmed) return;
    setBusy(true);
    try {
      const result = await postPointBlankJson('/control/files/abort', {
        targets: agent ? [agent.agent_id] : 'all'
      });
      const names = agents
        .filter(item => result.aborted.includes(item.agent_id))
        .map(item => item.label)
        .join(', ');
      setMessage(`Deployment aborted on ${names || 'no laptops'}.`);
      await refresh();
    } catch (err) {
      setMessage(err.message);
    } finally {
      setBusy(false);
    }
  };

  const formatSize = size => {
    const bytes = Number(size || 0);
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatDate = value => {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
  };

  return (
    <section className="pointblank-panel">
      <div className="pointblank-heading">
        <div>
          <h1>PointBlankSniper.lua</h1>
          <p>
            Uploads and deployments go to the laptops ticked below. Each laptop stops WoW, installs
            the file and returns to its previous run state on its own; offline laptops wait and update
            when they come back online. Up to ten versions are retained for rollback.
          </p>
        </div>
        <button type="button" onClick={() => refresh(true)} disabled={loading}>Refresh</button>
      </div>

      <div className="pointblank-upload">
        <input
          ref={fileInputRef}
          type="file"
          accept=".lua"
          onChange={event => setSelectedFile(event.target.files?.[0] || null)}
          disabled={busy}
        />
        <button type="button" onClick={upload} disabled={!canDeploy || !selectedFile}>
          {busy
            ? 'Working...'
            : `Upload and deploy to ${checkedAgents.length} laptop${checkedAgents.length === 1 ? '' : 's'}`}
        </button>
        <span>Required filename: PointBlankSniper.lua · maximum 20 MB</span>
        {lockedChecked.length > 0 && (
          <span className="pointblank-locked-note">
            A deployment is in progress on {lockedChecked.map(agent => agent.label).join(', ')}.
            Abort it or untick it to deploy.
          </span>
        )}
      </div>

      {message && <div className="pointblank-message">{message}</div>}

      {loading && !payload ? (
        <p>Loading file versions...</p>
      ) : payload && (
        <>
          <div className={`deployment-card ${payload.deployment.status}`}>
            <div className="deployment-heading">
              <div>
                <h2>Deployment: {payload.deployment.status}</h2>
                <p>{payload.deployment.message}</p>
              </div>
              {abortableAgents.length > 0 && (
                <button
                  type="button"
                  className="abort-all"
                  onClick={() => abortDeployment(null)}
                  disabled={busy}
                >
                  Abort all
                </button>
              )}
            </div>
            <div className="deployment-table-wrap">
              <table className="deployment-table">
                <thead>
                  <tr>
                    <th>Deploy</th>
                    <th>Laptop</th>
                    <th>Phase</th>
                    <th>Version</th>
                    <th>Time in phase</th>
                    <th>Last error</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {agents.map(agent => (
                    <tr key={agent.agent_id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={(checked || []).includes(agent.agent_id)}
                          onChange={() => toggleAgent(agent.agent_id)}
                          disabled={busy || !agent.file_updates_enabled}
                          aria-label={`Deploy to ${agent.label}`}
                        />
                      </td>
                      <td>
                        <strong>{agent.label}</strong>
                        <span>
                          {agent.alias} · {agent.online ? 'online' : 'offline'} · {agent.reported_status}
                        </span>
                        {!agent.file_updates_enabled && <span>File updates disabled</span>}
                        {agent.needs_agent_update && (
                          <span className="deployment-error">Needs the updated remote_agent.py</span>
                        )}
                      </td>
                      <td>
                        <span className={`phase-badge ${agent.phase || 'none'}`}>
                          {DEPLOY_PHASE_LABELS[agent.phase] || 'No deployment'}
                        </span>
                        {IN_PROGRESS_DEPLOY_PHASES.includes(agent.phase) && (
                          <span>Attempt {agent.attempts}/{agent.max_attempts}</span>
                        )}
                      </td>
                      <td>
                        <code>{agent.version_id || '-'}</code>
                        <span>Installed: {agent.file_version || 'not reported'}</span>
                      </td>
                      <td>{agent.phase ? formatElapsed(agent.seconds_in_phase) : '-'}</td>
                      <td>
                        {agent.last_error && <span className="deployment-error">{agent.last_error}</span>}
                      </td>
                      <td>
                        {agent.abortable && (
                          <button
                            type="button"
                            className="abort-target"
                            onClick={() => abortDeployment(agent)}
                            disabled={busy}
                          >
                            Abort
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="pointblank-history">
            <h2>Version history ({payload.versions.length}/{payload.max_versions})</h2>
            {payload.versions.length === 0 ? (
              <p>No PointBlankSniper.lua versions have been uploaded yet.</p>
            ) : payload.versions.map((version, index) => (
              <div className="pointblank-version" key={version.version_id}>
                <div>
                  <strong>{index === 0 ? 'Latest upload' : `Previous version ${index}`}</strong>
                  <code>{version.version_id}</code>
                </div>
                <span>{formatDate(version.uploaded_at)}</span>
                <span>{formatSize(version.size)} · SHA-256 {version.sha256.slice(0, 12)}…</span>
                <span>Uploaded through {version.source}</span>
                <button
                  type="button"
                  onClick={() => deployVersion(version)}
                  disabled={!canDeploy}
                  title={checkedNames ? `Deploy to ${checkedNames}` : 'Tick at least one laptop'}
                >
                  Deploy this version
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function App() {
  // ─── Region State ─────────────────────────────────────────
  const defaultRegion = localStorage.getItem('region') || 'na';
  const [region, setRegion] = useState(defaultRegion);
  const [listMode, setListMode] = useState(localStorage.getItem('listMode') || 'normal');
  const [activePage, setActivePage] = useState('items');
  useEffect(() => {
    localStorage.setItem('region', region);
  }, [region]);
  useEffect(() => {
    localStorage.setItem('listMode', listMode);
  }, [listMode]);
  const toggleRegion = () => setRegion(prev => (prev === 'na' ? 'eu' : 'na'));
  const openList = mode => {
    setListMode(mode);
    setActivePage('items');
  };
  const isHighPriority = listMode === 'highPriority';
  const isSpecialList = listMode === 'specialList';
  const showsVariants = isHighPriority || isSpecialList;

  // ─── Data & UI State ───────────────────────────────────────
  const [items, setItems]             = useState([]);
  const [pendingData, setPendingData] = useState({});
  const [sortConfig, setSortConfig]   = useState({ key: null, direction: 'desc' });
  const [editingCell, setEditingCell] = useState({ rowIndex: null, key: null });
  const [foldStates, setFoldStates]   = useState({});
  const [ctxMenu, setCtxMenu]         = useState({ visible: false, x: 0, y: 0, rowIndex: null, groupKey: null });
  const [priceMenu, setPriceMenu]     = useState({ visible: false, x: 0, y: 0, rowIndex: null });
  const [autoModal, setAutoModal]     = useState({ ...EMPTY_AUTO_MODAL });
  const [variantModal, setVariantModal] = useState({ ...EMPTY_VARIANT_MODAL });
  const [ilvlVariationModal, setIlvlVariationModal] = useState({ ...EMPTY_ILVL_VARIATION });

  const [merged, setMerged]             = useState(true);
  const [expandedRows, setExpandedRows] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [newItem, setNewItem]           = useState(EMPTY_ITEM);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importInput, setImportInput]         = useState('');
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [pricingFormula, setPricingFormula]     = useState('value_ladder');
  const [profitTarget, setProfitTarget]         = useState('75000');
  const [pricingExclusions, setPricingExclusions] = useState({});
  const [pricingStatus, setPricingStatus]       = useState('');
  const [selectionMode, setSelectionMode]         = useState(false);
  const [selectedItems, setSelectedItems]         = useState({});
  const [bulkStatus, setBulkStatus]               = useState('');
  const [specialItems, setSpecialItems]           = useState([]);
  const [error, setError]                     = useState(null);
  const ctxMenuRef = useRef(null);
  const priceMenuRef = useRef(null);

  useLayoutEffect(() => {
    if (!ctxMenu.visible || !ctxMenuRef.current) return;
    const margin = 8;
    const bounds = ctxMenuRef.current.getBoundingClientRect();
    const x = Math.max(margin, Math.min(ctxMenu.x, window.innerWidth - bounds.width - margin));
    const y = Math.max(margin, Math.min(ctxMenu.y, window.innerHeight - bounds.height - margin));
    if (x !== ctxMenu.x || y !== ctxMenu.y) {
      setCtxMenu(current => ({ ...current, x, y }));
    }
  }, [ctxMenu.visible, ctxMenu.x, ctxMenu.y]);

  useLayoutEffect(() => {
    if (!priceMenu.visible || !priceMenuRef.current) return;
    const margin = 8;
    const bounds = priceMenuRef.current.getBoundingClientRect();
    const x = Math.max(margin, Math.min(priceMenu.x, window.innerWidth - bounds.width - margin));
    const y = Math.max(margin, Math.min(priceMenu.y, window.innerHeight - bounds.height - margin));
    if (x !== priceMenu.x || y !== priceMenu.y) {
      setPriceMenu(current => ({ ...current, x, y }));
    }
  }, [priceMenu.visible, priceMenu.x, priceMenu.y]);

  useEffect(() => {
    setSelectionMode(false);
    setSelectedItems({});
    setBulkStatus('');
    setIlvlVariationModal({ ...EMPTY_ILVL_VARIATION });
  }, [region, listMode]);

  const getItemIdentity = item => {
    const base = item.id != null
      ? `id:${item.id}`
      : item.pet_species_id != null
        ? `pet:${item.pet_species_id}`
        : `name:${item.name || ''}`;
    return [
      base,
      `ilvl:${item.ilvl ?? 0}`,
      `socket:${item.socket ? 1 : 0}`,
      `stat:${item.extra_stat || ''}`
    ].join('|');
  };
  const pricingExclusionsStorageKey = useMemo(
    () => `wowsite:pricingExclusions:${region}:${listMode}`,
    [region, listMode]
  );
  const priceSnapshotStorageKey = useMemo(
    () => `wowsite:priceSnapshot:${region}:${listMode}`,
    [region, listMode]
  );
  const getVariantLabel = item => {
    const parts = [];
    if (item.socket) parts.push('Socket');
    if (item.extra_stat) {
      if (item.extra_stat === 'any') parts.push('Any secondary (no Indestructible)');
      else if (item.extra_stat === 'any_or_socket') parts.push('Any secondary or socket');
      else parts.push(item.extra_stat[0].toUpperCase() + item.extra_stat.slice(1));
    }
    return parts.join(' + ') || 'Base';
  };
  const isFolded = key => isVariantGroupKey(key)
    ? foldStates[key] !== false
    : Boolean(foldStates[key]);
  const getPriorityParentValue = item =>
    item.parent ?? !(item.socket || item.extra_stat);
  const getAutomatedPriceSign = item =>
    item.automatedprice_sign === 'plus' ? 'plus' : 'minus';
  const isPriorityChild = item =>
    isHighPriority &&
    !getPriorityParentValue(item) &&
    Boolean(item.socket || item.extra_stat);
  const getAutomationAverage = item => {
    if (!isPriorityChild(item)) return item.min_n_avg_gold;
    const parent = items.find(candidate =>
      getKey(candidate) === getKey(item) &&
      Number(candidate.ilvl) === Number(item.ilvl) &&
      getPriorityParentValue(candidate) &&
      !candidate.socket &&
      !candidate.extra_stat
    );
    return parent?.min_n_avg_gold ?? null;
  };
  const getSelectedListItems = () =>
    items.filter(item => selectedItems[getItemIdentity(item)]);
  const selectedCount = items.reduce(
    (count, item) => count + (selectedItems[getItemIdentity(item)] ? 1 : 0),
    0
  );
  const specialItemKeys = useMemo(
    () => new Set(specialItems.map(getItemIdentity)),
    [specialItems]
  );
  const isSpecialAlertItem = item => specialItemKeys.has(getItemIdentity(item));
  const getContextActionItems = (item, groupKey = null) => {
    const selected = getSelectedListItems();
    if (selectionMode && selected.length > 0) return selected;
    if (item) return [item];
    if (isVariantGroupKey(groupKey)) {
      return items.filter(target => getVariantGroupKey(target) === groupKey);
    }
    if (groupKey != null) return items.filter(target => getKey(target) === groupKey);
    return [];
  };
  const isDuplicateError = message =>
    String(message || '').toLowerCase().includes('duplicate');
  const toggleSelectedItem = item => {
    const identity = getItemIdentity(item);
    setSelectedItems(prev => {
      const next = { ...prev };
      if (next[identity]) delete next[identity];
      else next[identity] = true;
      return next;
    });
  };
  const toggleSelectedGroup = groupItems => {
    const identities = groupItems.map(getItemIdentity);
    const allSelected = identities.every(identity => selectedItems[identity]);
    setSelectedItems(prev => {
      const next = { ...prev };
      identities.forEach(identity => {
        if (allSelected) delete next[identity];
        else next[identity] = true;
      });
      return next;
    });
  };
  const getListEndpoint = action => {
    const endpoints = isHighPriority
      ? {
          get: 'gethighprioritylist',
          add: 'addhighpriorityitem',
          edit: 'edithighpriorityitem',
          delete: 'deletehighpriorityitem'
        }
      : {
          get: 'getitemslist',
          add: 'additem',
          edit: 'edititem',
          delete: 'deleteitem'
        };
    return `${API_BASE}/${endpoints[action]}`;
  };
  const buildItemPayload = item => ({
    region,
    name: item.name,
    id: item.id,
    pet_species_id: item.pet_species_id,
    ilvl: item.ilvl,
    desired_price: item.desired_price,
    socket: item.socket || false,
    extra_stat: item.extra_stat || '',
    parent: item.parent || false,
    variant_group: item.variant_group || ''
  });
  const buildLookupPayload = item => {
    const payload = { region, lookup_ilvl: item.ilvl };
    if (item.id != null) payload.lookup_id = item.id;
    else if (item.pet_species_id != null) payload.lookup_pet_species_id = item.pet_species_id;
    else payload.lookup_name = item.name;
    return payload;
  };
  const formatGold = value =>
    Number.isFinite(Number(value)) ? Math.round(Number(value)).toLocaleString() : '';
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const normalizeAutomatedPercentage = (value, sign) => {
    const parsed = Number.parseFloat(value);
    if (!Number.isFinite(parsed)) return 0;
    const nonnegative = Math.max(0, parsed);
    return sign === 'plus' ? nonnegative : Math.min(100, nonnegative);
  };
  const getBaseProfitTarget = () => Math.max(0, Number(profitTarget) || 0);
  const getFormulaProfitTarget = avg => {
    const baseTarget = getBaseProfitTarget();
    if (pricingFormula === 'profit_band') {
      return avg >= 1000000 ? baseTarget * (4 / 3) : avg >= 300000 ? baseTarget : baseTarget * (2 / 3);
    }
    if (pricingFormula === 'high_value_margin') {
      return avg >= 2000000 ? baseTarget * 5 : avg >= 1000000 ? baseTarget * 3 : avg >= 300000 ? baseTarget * 1.8 : baseTarget;
    }
    return baseTarget;
  };
  const getPricingFormulaResult = item => {
    const avg = Number(getAutomationAverage(item));
    if (!Number.isFinite(avg) || avg <= 0) return null;
    const resaleAfterCut = avg * ESTIMATED_RESALE_RATE * AH_SALE_CUT_RATE;

    let pct;
    let desiredPrice = null;
    if (pricingFormula === 'value_ladder') {
      pct = avg >= 1000000 ? 30 : avg >= 300000 ? 45 : avg >= 100000 ? 55 : 65;
    } else if (pricingFormula === 'profit_band') {
      const target = getFormulaProfitTarget(avg);
      desiredPrice = resaleAfterCut - target;
      pct = ((avg - desiredPrice) / avg) * 100;
    } else if (pricingFormula === 'high_value_margin') {
      const target = getFormulaProfitTarget(avg);
      desiredPrice = resaleAfterCut - target;
      pct = ((avg - desiredPrice) / avg) * 100;
    } else if (pricingFormula === 'conservative') {
      pct = avg >= 1000000 ? 25 : avg >= 300000 ? 35 : avg >= 100000 ? 45 : 55;
    } else if (pricingFormula === 'aggressive') {
      pct = avg >= 1000000 ? 40 : avg >= 300000 ? 55 : avg >= 100000 ? 65 : 75;
    } else {
      const target = getFormulaProfitTarget(avg);
      desiredPrice = resaleAfterCut - target;
      pct = ((avg - desiredPrice) / avg) * 100;
    }

    pct = clamp(pct, 0, 95);
    return {
      automatedprice: Number(pct.toFixed(2)),
      automatedprice_sign: 'minus',
      desired_price: Math.max(1, Math.round(avg * (1 - pct / 100)))
    };
  };
  const automatedItems = useMemo(
    () => items.filter(item => item.automatedprice != null),
    [items]
  );

  // ─── Fetch items whenever region changes ─────────────────────
  useEffect(() => {
    // clear out any in‑flight edits / errors when region toggles
    setPendingData({});
    setError(null);

    const endpoint = isSpecialList
      ? `${API_BASE}/getspecialitemslist`
      : isHighPriority
        ? `${API_BASE}/gethighprioritylist`
        : `${API_BASE}/getitemslist`;
    fetch(`${endpoint}?region=${region}`)
      .then(res => res.json())
      .then(data => {
        if (data.error) setError(data.error);
        else {
          setItems(data);
          if (isSpecialList) setSpecialItems(data);
        }
      })
      .catch(err => setError(err.message || 'Failed to load items'));
  }, [region, isHighPriority, isSpecialList]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/getspecialitemslist?region=${region}`)
      .then(res => res.json())
      .then(data => {
        if (cancelled) return;
        if (data.error) setError(data.error);
        else setSpecialItems(data);
      })
      .catch(err => {
        if (!cancelled) setError(err.message || 'Failed to load special alert items');
      });
    return () => {
      cancelled = true;
    };
  }, [region]);

  useEffect(() => {
    try {
      setPricingExclusions(JSON.parse(localStorage.getItem(pricingExclusionsStorageKey) || '{}'));
    } catch {
      setPricingExclusions({});
    }
    setPricingStatus('');
  }, [pricingExclusionsStorageKey]);

  useEffect(() => {
    localStorage.setItem(
      pricingExclusionsStorageKey,
      JSON.stringify(pricingExclusions)
    );
  }, [pricingExclusions, pricingExclusionsStorageKey]);

  // ─── Hide context menus ────────────────────────────────────
  useEffect(() => {
    const onClick = () => {
      setCtxMenu({ visible: false, x: 0, y: 0, rowIndex: null, groupKey: null });
      setPriceMenu({ visible: false, x: 0, y: 0, rowIndex: null });
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  // ─── Sorting & Folding ─────────────────────────────────────
  const sortedItems = useMemo(() => {
    if (!sortConfig.key) return items;
    return [...items].sort((a, b) => {
      const dir = sortConfig.direction === 'desc' ? -1 : 1;
      const aVal = a[sortConfig.key] ?? 0;
      const bVal = b[sortConfig.key] ?? 0;
      return (aVal - bVal) * dir;
    });
  }, [items, sortConfig]);

  const displayedRows = useMemo(() => {
    const rows = [], seen = new Set();
    if (showsVariants) {
      const getFamilyKey = item => `${getKey(item)}|${item.ilvl ?? 0}`;
      const isVariantChild = item => !item.parent && (item.socket || item.extra_stat);
      const childBuckets = new Map();
      const roots = [];

      sortedItems.forEach(item => {
        if (isVariantChild(item)) {
          const familyKey = getFamilyKey(item);
          if (!childBuckets.has(familyKey)) childBuckets.set(familyKey, []);
          childBuckets.get(familyKey).push(item);
        } else {
          roots.push(item);
        }
      });

      const appendChildren = (children, isChild, isOrphan = false) => {
        const emittedGroups = new Set();
        children.forEach(child => {
          const groupKey = getVariantGroupKey(child);
          if (!groupKey) {
            rows.push({ type: 'item', item: child, isChild, isOrphan });
            return;
          }
          if (emittedGroups.has(groupKey)) return;
          emittedGroups.add(groupKey);
          const groupItems = children.filter(candidate => getVariantGroupKey(candidate) === groupKey);
          if (foldStates[groupKey] !== false) {
            rows.push({
              type: 'variant-group',
              key: groupKey,
              label: VARIANT_GROUP_LABELS[child.variant_group] || getVariantLabel(child),
              items: groupItems,
              isChild,
              isOrphan
            });
          } else {
            groupItems.forEach(item => rows.push({ type: 'item', item, isChild, isOrphan }));
          }
        });
      };

      roots.forEach(item => {
        rows.push({ type: 'item', item });
        const familyKey = getFamilyKey(item);
        appendChildren(childBuckets.get(familyKey) || [], true);
        childBuckets.delete(familyKey);
      });

      for (const children of childBuckets.values()) {
        appendChildren(children, false, true);
      }

      return rows;
    }

    sortedItems.forEach(item => {
      const key = getKey(item);
      if (foldStates[key]) {
        if (!seen.has(key)) {
          const group = sortedItems.filter(x => getKey(x) === key);
          rows.push({ type: 'group', key, name: item.name, items: group });
          seen.add(key);
        }
      } else {
        rows.push({ type: 'item', item });
      }
    });
    return rows;
  }, [sortedItems, foldStates, showsVariants]);

  // ─── Handlers ──────────────────────────────────────────────
  const toggleSort = key =>
    setSortConfig(prev =>
      prev.key === key
        ? { key, direction: prev.direction === 'desc' ? 'asc' : 'desc' }
        : { key, direction: 'desc' }
    );

  const handleContextMenu = (e, rowIndex, groupKey = null) => {
    e.preventDefault();
    setCtxMenu({ visible: true, x: e.clientX, y: e.clientY, rowIndex, groupKey });
  };
  const isInteractiveElement = target =>
    Boolean(target.closest('button, input, select, textarea, a'));
  const handleSelectionRowClick = (e, item) => {
    if (!selectionMode || isInteractiveElement(e.target)) return;
    toggleSelectedItem(item);
  };
  const handlePriceContextMenu = (e, rowIndex) => {
    e.preventDefault();
    e.stopPropagation();
    if (isSpecialList) return;
    setPriceMenu({ visible: true, x: e.clientX, y: e.clientY, rowIndex });
  };

  const toggleFold = id =>
    setFoldStates(prev => ({
      ...prev,
      [id]: isVariantGroupKey(id) ? prev[id] === false : !prev[id]
    }));

  // Add this:
  const toggleExpand = rowIndex =>
    setExpandedRows(prev => ({ ...prev, [rowIndex]: !prev[rowIndex] }));

  const handleCellDoubleClick = (rowIndex, key) => {
    if (isSpecialList) return;
    setEditingCell({ rowIndex, key });
  };

  const queueChange = (rowIndex, updated) => {
    setPendingData(pd =>
      pd.hasOwnProperty(rowIndex) ? pd : { ...pd, [rowIndex]: items[rowIndex] }
    );
    setItems(prev => prev.map((it, i) => (i === rowIndex ? updated : it)));
    setEditingCell({ rowIndex: null, key: null });
  };

  const commitChange = (value, rowIndex, key) => {
    const orig = items[rowIndex];
    const upd = { ...orig };
    if (key === 'id' || key === 'pet_species_id') {
      upd[key] = value.trim() === '' ? null : Number(value);
    } else if (key === 'ilvl' || key === 'desired_price') {
      upd[key] = value.trim() === '' ? '' : Number(value);
    } else {
      upd[key] = value.trim();
    }
    if (upd.ilvl === '' || isNaN(upd.ilvl)) {
      setError('iLvl is required and must be numeric');
      setEditingCell({ rowIndex: null, key: null });
      return;
    }
    if (!(upd.id != null || upd.pet_species_id != null || upd.name.trim())) {
      setError('At least one of Name, ID, or Pet ID must be present');
      setEditingCell({ rowIndex: null, key: null });
      return;
    }
    if (
      orig.name === upd.name &&
      (orig.id ?? null) === upd.id &&
      (orig.pet_species_id ?? null) === upd.pet_species_id &&
      orig.ilvl === upd.ilvl &&
      orig.desired_price === upd.desired_price
    ) {
      setEditingCell({ rowIndex: null, key: null });
      return;
    }
    queueChange(rowIndex, upd);
  };

  const commitCombined = (rowIndex, idVal, petVal) => {
    const orig = items[rowIndex];
    let newId = idVal.trim() === '' ? null : Number(idVal);
    let newPet = petVal.trim() === '' ? null : Number(petVal);
    if (newId != null) newPet = null;
    else if (newPet != null) newId = null;
    const upd = { ...orig, id: newId, pet_species_id: newPet };
    if (upd.ilvl === '' || isNaN(upd.ilvl)) {
      setError('iLvl is required and must be numeric');
      setEditingCell({ rowIndex: null, key: null });
      return;
    }
    if (!(upd.id != null || upd.pet_species_id != null || upd.name.trim())) {
      setError('At least one of Name, ID, or Pet ID must be present');
      setEditingCell({ rowIndex: null, key: null });
      return;
    }
    if (
      orig.name === upd.name &&
      (orig.id ?? null) === upd.id &&
      (orig.pet_species_id ?? null) === upd.pet_species_id &&
      orig.ilvl === upd.ilvl &&
      orig.desired_price === upd.desired_price
    ) {
      setEditingCell({ rowIndex: null, key: null });
      return;
    }
    queueChange(rowIndex, upd);
  };

  const handleSave = async rowIndex => {
    const orig = pendingData[rowIndex];
    const upd  = items[rowIndex];
    const lookup = { region, lookup_ilvl: orig.ilvl };
    if (orig.id != null) lookup.lookup_id = orig.id;
    else if (orig.pet_species_id != null) lookup.lookup_pet_species_id = orig.pet_species_id;
    else lookup.lookup_name = orig.name;
    if (isHighPriority) {
      lookup.lookup_socket = orig.socket || false;
      lookup.lookup_extra_stat = orig.extra_stat || '';
    }

    const body = {
      ...lookup,
      new_name:           upd.name,
      new_id:             upd.id,
      new_pet_species_id: upd.pet_species_id,
      new_ilvl:           upd.ilvl,
      new_desired_price:  upd.desired_price,
      new_socket:         upd.socket || false,
      new_extra_stat:     upd.extra_stat || '',
      new_parent:         isHighPriority ? getPriorityParentValue(upd) : false
    };

    try {
      const res = await fetch(getListEndpoint('edit'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) setError(data.error || 'Edit failed');
      else {
        setItems(data);
        if (!isHighPriority) {
          await syncHighPriorityParent(upd);
        }
        setPendingData(pd => {
          const next = { ...pd };
          delete next[rowIndex];
          return next;
        });
      }
    } catch (e) {
      setError(e.message || 'Network error');
    }
  };

  const handleDelete = async item => {
    if (isSpecialList) {
      await removeFromSpecialList(item);
      return;
    }

    const payload = { region, ilvl: item.ilvl };
    if (item.id != null) payload.id = item.id;
    else if (item.pet_species_id != null) payload.pet_species_id = item.pet_species_id;
    else payload.name = item.name;
    if (isHighPriority) {
      payload.name = item.name;
      payload.desired_price = item.desired_price;
      payload.socket = item.socket || false;
      payload.extra_stat = item.extra_stat || '';
      payload.parent = getPriorityParentValue(item);
      payload.delete_family = Boolean(payload.parent || (!payload.socket && !payload.extra_stat));
    }

    try {
      const res = await fetch(getListEndpoint('delete'), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) setError(data.error || 'Delete failed');
      else setItems(data);
    } catch (e) {
      setError(e.message || 'Network error');
    }
  };

  const openAdd    = () => setShowAddModal(true);
  const closeAdd   = () => {
    setShowAddModal(false);
    setNewItem({ ...EMPTY_ITEM });
  };
  const addItemToList = async (payload, targetRegion, options = {}) => {
    const endpoint = isHighPriority ? 'addhighpriorityitem' : 'additem';
    const res = await fetch(`${API_BASE}/${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...payload, region: targetRegion }),
    });
    const data = await res.json();
    if (!res.ok) {
      const error = data.error || 'Add failed';
      if (options.ignoreDuplicate && isDuplicateError(error)) {
        return { ok: true, duplicate: true, data: null };
      }
      return { ok: false, error };
    }
    return { ok: true, duplicate: false, data };
  };
  const confirmAdd = async () => {
    const { name, id, pet_species_id, ilvl, desired_price, socket, extra_stat, add_other_region } = newItem;
    const idT   = id.trim();
    const petT  = pet_species_id.trim();
    const prT   = desired_price.trim();

    if ((!idT && !petT) || !prT) {
      setError('(ID or Pet ID) and Desired Price are required');
      return;
    }
    if (isHighPriority && (socket || extra_stat) && Number(ilvl) === 0) {
      setError('Socket/extra stat variants require a non-zero iLvl');
      return;
    }

    const payload = {
      name:            name.trim(),
      id:              idT === ''  ? null : Number(idT),
      pet_species_id:  petT === ''  ? null : Number(petT),
      ilvl:            ilvl.trim() === '' ? null : Number(ilvl),
      desired_price:   Number(prT),
      socket:          socket || false,
      extra_stat:      extra_stat || '',
      parent:          isHighPriority && !(socket || extra_stat),
    };

    try {
      const otherRegion = region === 'na' ? 'eu' : 'na';
      const current = await addItemToList(payload, region, { ignoreDuplicate: add_other_region });
      const other = add_other_region
        ? await addItemToList(payload, otherRegion, { ignoreDuplicate: true })
        : null;

      if (!current.ok) {
        setError(current.error || 'Add failed');
        return;
      }
      if (other && !other.ok) {
        if (current.data) setItems(current.data);
        setError(other.error || `Add to ${otherRegion} failed`);
        return;
      }

      if (current.data) {
        setItems(current.data);
      }
      if (current.duplicate && !other?.data) {
        setBulkStatus(`Item already exists in ${add_other_region ? 'both regions' : region}`);
      } else if (other?.duplicate) {
        setBulkStatus(`Added item; it already existed in ${otherRegion}`);
      } else if (other?.data) {
        setBulkStatus(`Added item to ${region.toUpperCase()} and ${otherRegion.toUpperCase()}`);
      } else {
        setBulkStatus('');
      }
        closeAdd();
    } catch (e) {
      setError(e.message || 'Network error');
    }
  };

  const openImport  = () => setShowImportModal(true);
  const closeImport = () => { setShowImportModal(false); setImportInput(''); };
  const confirmImport = async () => {
    const raw = importInput.trim();
    if (!raw) return;
    try {
      const res = await fetch(`${API_BASE}/setlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ region, list: raw }),
      });
      const data = await res.json();
      if (!res.ok) setError(data.error || 'Import failed');
      else {
        setItems(data);
        closeImport();
      }
    } catch (e) {
      setError(e.message || 'Network error');
    }
  };

  const openAutomatedModal = targetItems => {
    const targets = (Array.isArray(targetItems) ? targetItems : [targetItems]).filter(Boolean);
    if (!targets.length) return;
    const first = targets[0];
    const firstPct = first.automatedprice != null ? String(first.automatedprice) : '';
    const firstSign = first.automatedprice != null ? getAutomatedPriceSign(first) : 'minus';
    const hasCommonAutomation = targets.every(item =>
      (item.automatedprice != null ? String(item.automatedprice) : '') === firstPct &&
      (item.automatedprice != null ? getAutomatedPriceSign(item) : 'minus') === firstSign
    );
    setAutoModal({
      ...EMPTY_AUTO_MODAL,
      visible: true,
      rowIndex: targets.length === 1 ? items.indexOf(first) : null,
      items: targets,
      pct: hasCommonAutomation ? firstPct : '',
      sign: hasCommonAutomation ? firstSign : 'minus'
    });
  };

  const confirmAutomated = async () => {
    if (autoModal.submitting) return;
    const targets = autoModal.items.length
      ? autoModal.items
      : autoModal.rowIndex != null
        ? [items[autoModal.rowIndex]]
        : [];
    if (!targets.length) {
      setAutoModal({ ...EMPTY_AUTO_MODAL });
      return;
    }
    const automatedSign = autoModal.sign === 'plus' ? 'plus' : 'minus';
    const pctNum = normalizeAutomatedPercentage(autoModal.pct, automatedSign);
    const priceMultiplier = automatedSign === 'plus'
      ? 1 + pctNum / 100
      : 1 - pctNum / 100;
    let latestItems = null;
    let updated = 0;
    const failures = [];
    setAutoModal(current => ({ ...current, submitting: true }));

    for (const item of targets) {
      const lookup = { region, lookup_ilvl: item.ilvl };
      if (item.id != null) lookup.lookup_id = item.id;
      else if (item.pet_species_id != null) lookup.lookup_pet_species_id = item.pet_species_id;
      else lookup.lookup_name = item.name;
      if (isHighPriority) {
        lookup.lookup_name = item.name;
        lookup.lookup_socket = item.socket || false;
        lookup.lookup_extra_stat = item.extra_stat || '';
      }

      const automationAverage = getAutomationAverage(item);
      const newDesired = automationAverage != null
        ? Math.round(automationAverage * priceMultiplier)
        : item.desired_price;
      const payload = {
        ...lookup,
        new_name:           item.name,
        new_id:             item.id,
        new_pet_species_id: item.pet_species_id,
        new_ilvl:           item.ilvl,
        new_desired_price:  newDesired,
        new_socket:         item.socket || false,
        new_extra_stat:     item.extra_stat || '',
        new_parent:         isHighPriority ? getPriorityParentValue(item) : false,
        automatedprice:     pctNum,
        automatedprice_sign: automatedSign
      };

      try {
        const res = await fetch(getListEndpoint('edit'), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (!res.ok) {
          failures.push(data.error || getVariantLabel(item));
          continue;
        }
        latestItems = data;
        updated += 1;
        if (!isHighPriority) {
          await syncHighPriorityParent({
            ...item,
            desired_price: newDesired,
            automatedprice: pctNum,
            automatedprice_sign: automatedSign
          }, {
            automatedprice: pctNum,
            automatedprice_sign: automatedSign
          });
        }
      } catch (e) {
        failures.push(e.message || getVariantLabel(item));
      }
    }

    if (latestItems) setItems(latestItems);
    setAutoModal({ ...EMPTY_AUTO_MODAL });
    if (targets.length > 1) {
      setBulkStatus(`Automated price updated for ${updated} of ${targets.length} children.`);
    }
    if (failures.length) {
      setError(`Failed to update automated price for ${failures.length} of ${targets.length} item${targets.length === 1 ? '' : 's'}.`);
    }
  };

  const cancelAutomatedPrice = async idx => {
    const item = items[idx];
    const lookup = { region, lookup_ilvl: item.ilvl };
    if (item.id != null) lookup.lookup_id = item.id;
    else if (item.pet_species_id != null) lookup.lookup_pet_species_id = item.pet_species_id;
    else lookup.lookup_name = item.name;
    if (isHighPriority) {
      lookup.lookup_name = item.name;
      lookup.lookup_socket = item.socket || false;
      lookup.lookup_extra_stat = item.extra_stat || '';
    }

    const body = {
      ...lookup,
      new_name:           item.name,
      new_id:             item.id,
      new_pet_species_id: item.pet_species_id,
      new_ilvl:           item.ilvl,
      new_desired_price:  item.desired_price,
      new_socket:         item.socket || false,
      new_extra_stat:     item.extra_stat || '',
      new_parent:         isHighPriority ? getPriorityParentValue(item) : false,
      automatedprice:     false,
      automatedprice_sign: 'minus'
    };

    try {
      const res = await fetch(getListEndpoint('edit'), {
        method:  'PUT',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) setError(data.error || 'Failed to cancel automated price');
      else {
        setItems(data);
        if (!isHighPriority) {
          await syncHighPriorityParent(item, { automatedprice: false });
        }
      }
    } catch (e) {
      setError(e.message || 'Network error');
    }
  };

  const addToHighPriority = async (item, overrides = {}) => {
    const targetRegion = overrides.region || region;
    const isParent = overrides.parent ?? item.parent ?? false;
    const payload = {
      ...buildItemPayload(item),
      region: targetRegion,
      desired_price: overrides.desired_price ?? item.desired_price,
      socket: overrides.socket ?? item.socket ?? false,
      extra_stat: overrides.extra_stat ?? item.extra_stat ?? '',
      parent: isParent,
      variant_group: overrides.variant_group ?? item.variant_group ?? ''
    };
    if (item.min_n_avg_gold != null) {
      payload.min_n_avg_gold = item.min_n_avg_gold;
    }
    if (Array.isArray(item.ten_lowest)) {
      payload.ten_lowest = item.ten_lowest;
    }
    if (Object.prototype.hasOwnProperty.call(overrides, 'automatedprice')) {
      payload.automatedprice = overrides.automatedprice;
      payload.automatedprice_sign = overrides.automatedprice_sign === 'plus' ? 'plus' : 'minus';
    } else if (item.automatedprice != null) {
      payload.automatedprice = item.automatedprice;
      payload.automatedprice_sign = getAutomatedPriceSign(item);
    }
    if ((payload.socket || payload.extra_stat) && Number(payload.ilvl) === 0) {
      setError('Socket/extra stat variants require a non-zero iLvl');
      return false;
    }

    try {
      const res = await fetch(`${API_BASE}/addhighpriorityitem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) {
        if (overrides.ignoreDuplicate && (data.error || '').toLowerCase().includes('duplicate')) {
          return true;
        }
        setError(data.error || 'Failed to add high priority item');
        return false;
      }
      if (isHighPriority) setItems(data);
      return true;
    } catch (e) {
      setError(e.message || 'Network error');
      return false;
    }
  };

  const syncHighPriorityParent = async (item, options = {}) => {
    const body = {
      ...buildLookupPayload(item),
      lookup_socket: false,
      lookup_extra_stat: '',
      new_name: item.name,
      new_id: item.id,
      new_pet_species_id: item.pet_species_id,
      new_ilvl: item.ilvl,
      new_desired_price: item.desired_price,
      new_socket: false,
      new_extra_stat: '',
      new_parent: true
    };
    if (Object.prototype.hasOwnProperty.call(options, 'automatedprice')) {
      body.automatedprice = options.automatedprice;
      body.automatedprice_sign = options.automatedprice_sign === 'plus' ? 'plus' : 'minus';
    }

    try {
      const res = await fetch(`${API_BASE}/edithighpriorityitem`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (res.status === 404) return;
      const data = await res.json();
      if (!res.ok) setError(data.error || 'Failed to sync high priority parent');
    } catch (e) {
      setError(e.message || 'Network error');
    }
  };

  const openVariantModal = targetItems => {
    const targets = Array.isArray(targetItems) ? targetItems : targetItems ? [targetItems] : [];
    if (!targets.length) return;
    const validTargets = targets.filter(item => Number(item.ilvl) !== 0);
    if (!validTargets.length) {
      setError('Socket/extra stat variants require a non-zero iLvl');
      return;
    }
    if (validTargets.length !== targets.length) {
      setBulkStatus(`Skipped ${targets.length - validTargets.length} selected item${targets.length - validTargets.length === 1 ? '' : 's'} with zero iLvl`);
    }
    const first = validTargets[0];
    setVariantModal({
      ...EMPTY_VARIANT_MODAL,
      visible: true,
      items: validTargets,
      socket: first.socket || false,
      extra_stat: first.extra_stat || ''
    });
  };

  const confirmVariant = async () => {
    const targets = variantModal.items || [];
    const secondaryStats = ['avoidance', 'speed', 'leech'];
    let variants;
    if (variantModal.extra_stat === 'any') {
      variants = secondaryStats.map(extra_stat => ({
        socket: variantModal.socket,
        extra_stat,
        variant_group: variantModal.socket ? 'socket_any_secondary' : 'any_secondary'
      }));
    } else if (variantModal.extra_stat === 'any_or_socket') {
      variants = [
        ...secondaryStats.map(extra_stat => ({
          socket: false,
          extra_stat,
          variant_group: 'any_secondary_or_socket'
        })),
        ...['', 'avoidance', 'speed', 'leech', 'indestructible'].map(extra_stat => ({
          socket: true,
          extra_stat,
          variant_group: 'any_secondary_or_socket'
        }))
      ];
    } else {
      variants = [{
        socket: variantModal.socket,
        extra_stat: variantModal.extra_stat,
        variant_group: ''
      }];
    }

    const hasAutomatedPrice = String(variantModal.automatedprice).trim() !== '';
    const automatedSign = variantModal.automatedprice_sign === 'plus' ? 'plus' : 'minus';
    const automatedPct = hasAutomatedPrice
      ? normalizeAutomatedPercentage(variantModal.automatedprice, automatedSign)
      : null;
    let added = 0;
    let skipped = 0;

    for (const item of targets) {
      if (!isHighPriority) {
        await addToHighPriority(item, {
          socket: false,
          extra_stat: '',
          parent: true,
          ignoreDuplicate: true
        });
      }
      for (const variant of variants) {
        const overrides = {
          ...variant,
          parent: false,
          ignoreDuplicate: targets.length > 1 || variants.length > 1
        };
        if (hasAutomatedPrice) {
          const automationAverage = getAutomationAverage(item);
          const multiplier = automatedSign === 'plus'
            ? 1 + automatedPct / 100
            : 1 - automatedPct / 100;
          overrides.automatedprice = automatedPct;
          overrides.automatedprice_sign = automatedSign;
          if (automationAverage != null) {
            overrides.desired_price = Math.round(automationAverage * multiplier);
          }
        }
        const ok = await addToHighPriority(item, overrides);
        if (ok) added += 1;
        else skipped += 1;
      }
    }

    setVariantModal({ ...EMPTY_VARIANT_MODAL });
    const totalRequested = targets.length * variants.length;
    setBulkStatus(
      totalRequested > 1
        ? `Variant request finished: ${added} variation${added === 1 ? '' : 's'} added or already present${skipped ? `, ${skipped} failed` : ''}`
        : ''
    );
  };

  const openIlvlVariationModal = targetItems => {
    const targets = Array.isArray(targetItems) ? targetItems : targetItems ? [targetItems] : [];
    if (!targets.length) return;
    setIlvlVariationModal({
      ...EMPTY_ILVL_VARIATION,
      visible: true,
      items: targets
    });
  };

  const closeIlvlVariationModal = () => {
    if (ilvlVariationModal.submitting) return;
    setIlvlVariationModal({ ...EMPTY_ILVL_VARIATION });
  };

  const confirmIlvlVariations = async () => {
    if (ilvlVariationModal.submitting) return;

    const tokens = ilvlVariationModal.ilvls
      .split(',')
      .map(value => value.trim())
      .filter(Boolean);
    const invalid = tokens.find(value => !/^\d+$/.test(value));
    if (!tokens.length) {
      setError('Enter at least one iLvl');
      return;
    }
    if (invalid != null) {
      setError(`Invalid iLvl: ${invalid}`);
      return;
    }

    const ilvls = [...new Set(tokens.map(Number))];
    setIlvlVariationModal(current => ({ ...current, submitting: true }));

    try {
      const res = await fetch(`${API_BASE}/addilvlvariations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          region,
          list: isHighPriority ? 'highpriority' : 'normal',
          items: ilvlVariationModal.items.map(item => ({
            name: item.name,
            id: item.id,
            pet_species_id: item.pet_species_id,
            ilvl: item.ilvl,
            socket: item.socket || false,
            extra_stat: item.extra_stat || '',
            parent: isHighPriority ? getPriorityParentValue(item) : false
          })),
          ilvls,
          desired_price_mode: ilvlVariationModal.desired_price_mode,
          add_other_region: ilvlVariationModal.add_other_region
        })
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to add iLvl variations');
        setIlvlVariationModal(current => ({ ...current, submitting: false }));
        return;
      }

      if (Array.isArray(data.items)) setItems(data.items);

      const regionEntries = Object.entries(data.regions || {});
      const total = key => regionEntries.reduce(
        (sum, [, stats]) => sum + Number(stats[key] || 0),
        0
      );
      const added = total('added');
      const duplicates = total('duplicates');
      const missingParents = total('missing_parents');
      const statusParts = [
        added
          ? `Added ${added} iLvl variation${added === 1 ? '' : 's'}`
          : 'No iLvl variations added'
      ];
      if (ilvlVariationModal.add_other_region && regionEntries.length > 1 && added) {
        statusParts[0] += ` (${regionEntries.map(([code, stats]) => `${code.toUpperCase()} ${stats.added || 0}`).join(', ')})`;
      }
      if (duplicates) {
        statusParts.push(`skipped ${duplicates} already existing`);
      }
      regionEntries.forEach(([code, stats]) => {
        if (stats.missing_sources) {
          statusParts.push(`${stats.missing_sources} source item${stats.missing_sources === 1 ? '' : 's'} not found in ${code.toUpperCase()}`);
        }
        if (stats.error) statusParts.push(stats.error);
      });
      if (missingParents) {
        statusParts.push(`skipped ${missingParents} child variation${missingParents === 1 ? '' : 's'} without a parent`);
      }

      setIlvlVariationModal({ ...EMPTY_ILVL_VARIATION });
      setBulkStatus(statusParts.join('; '));
    } catch (e) {
      setError(e.message || 'Network error');
      setIlvlVariationModal(current => ({ ...current, submitting: false }));
    }
  };

  const addManyToHighPriority = async targetItems => {
    if (!targetItems.length) return;
    let addedOrExisting = 0;
    let failed = 0;
    for (const item of targetItems) {
      const ok = await addToHighPriority(item, {
        parent: true,
        ignoreDuplicate: targetItems.length > 1
      });
      if (ok) addedOrExisting += 1;
      else failed += 1;
    }
    setBulkStatus(
      targetItems.length > 1
        ? `High priority request finished for ${addedOrExisting} selected item${addedOrExisting === 1 ? '' : 's'}${failed ? `, ${failed} failed` : ''}`
        : ''
    );
  };

  const addToSpecialList = async (item, overrides = {}) => {
    const targetRegion = overrides.region || region;
    const payload = {
      ...buildItemPayload(item),
      region: targetRegion,
      socket: overrides.socket ?? item.socket ?? false,
      extra_stat: overrides.extra_stat ?? item.extra_stat ?? '',
      parent: overrides.parent ?? item.parent ?? false
    };
    if ((payload.socket || payload.extra_stat) && Number(payload.ilvl) === 0) {
      setError('Socket/extra stat variants require a non-zero iLvl');
      return false;
    }

    try {
      const res = await fetch(`${API_BASE}/addspecialitem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) {
        if (overrides.ignoreDuplicate && isDuplicateError(data.error)) {
          return true;
        }
        setError(data.error || 'Failed to add special alert item');
        return false;
      }
      if (targetRegion === region) setSpecialItems(data);
      return true;
    } catch (e) {
      setError(e.message || 'Network error');
      return false;
    }
  };

  const addManyToSpecialList = async targetItems => {
    if (!targetItems.length) return;
    let addedOrExisting = 0;
    let failed = 0;
    for (const item of targetItems) {
      const ok = await addToSpecialList(item, {
        ignoreDuplicate: targetItems.length > 1 || isSpecialAlertItem(item)
      });
      if (ok) addedOrExisting += 1;
      else failed += 1;
    }
    setBulkStatus(
      targetItems.length > 1
        ? `Special alert request finished for ${addedOrExisting} selected item${addedOrExisting === 1 ? '' : 's'}${failed ? `, ${failed} failed` : ''}`
        : ''
    );
  };

  const removeFromSpecialList = async (item, options = {}) => {
    const payload = {
      region,
      name: item.name,
      ilvl: item.ilvl,
      socket: item.socket || false,
      extra_stat: item.extra_stat || ''
    };
    if (item.id != null) payload.id = item.id;
    else if (item.pet_species_id != null) payload.pet_species_id = item.pet_species_id;

    try {
      const res = await fetch(`${API_BASE}/deletespecialitem`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) {
        if (options.ignoreMissing && String(data.error || '').toLowerCase().includes('not found')) {
          return true;
        }
        setError(data.error || 'Failed to remove special alert item');
        return false;
      }
      setSpecialItems(data);
      if (isSpecialList) setItems(data);
      return true;
    } catch (e) {
      setError(e.message || 'Network error');
      return false;
    }
  };

  const removeManyFromSpecialList = async targetItems => {
    if (!targetItems.length) return;
    let removed = 0;
    let failed = 0;
    for (const item of targetItems) {
      const ok = await removeFromSpecialList(item, {
        ignoreMissing: targetItems.length > 1 || !isSpecialAlertItem(item)
      });
      if (ok) removed += 1;
      else failed += 1;
    }
    setBulkStatus(
      targetItems.length > 1
        ? `Removed ${removed} selected special alert item${removed === 1 ? '' : 's'}${failed ? `, ${failed} failed` : ''}`
        : ''
    );
  };

  const deleteManyHighPriority = async targetItems => {
    if (!targetItems.length) return;
    let removed = 0;
    let failed = 0;
    for (const item of targetItems) {
      const payload = {
        region,
        name: item.name,
        ilvl: item.ilvl,
        desired_price: item.desired_price,
        socket: item.socket || false,
        extra_stat: item.extra_stat || '',
        parent: getPriorityParentValue(item),
        delete_family: Boolean(getPriorityParentValue(item) || (!item.socket && !item.extra_stat))
      };
      if (item.id != null) payload.id = item.id;
      else if (item.pet_species_id != null) payload.pet_species_id = item.pet_species_id;

      try {
        const res = await fetch(getListEndpoint('delete'), {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (!res.ok) {
          if (targetItems.length > 1 && String(data.error || '').toLowerCase().includes('not found')) {
            removed += 1;
            continue;
          }
          failed += 1;
        } else {
          removed += 1;
          setItems(data);
        }
      } catch {
        failed += 1;
      }
    }
    setSelectedItems({});
    setBulkStatus(
      targetItems.length > 1
        ? `Removed ${removed} selected high priority item${removed === 1 ? '' : 's'}${failed ? `, ${failed} failed` : ''}`
        : ''
    );
  };

  const updateItemPricing = async (
    item,
    desiredPrice,
    automatedpriceValue,
    automatedpriceSign = 'minus'
  ) => {
    const normalizedSign = automatedpriceSign === 'plus' ? 'plus' : 'minus';
    const lookup = buildLookupPayload(item);
    if (isHighPriority) {
      lookup.lookup_name = item.name;
      lookup.lookup_socket = item.socket || false;
      lookup.lookup_extra_stat = item.extra_stat || '';
    }

    const body = {
      ...lookup,
      new_name: item.name,
      new_id: item.id,
      new_pet_species_id: item.pet_species_id,
      new_ilvl: item.ilvl,
      new_desired_price: desiredPrice,
      new_socket: item.socket || false,
      new_extra_stat: item.extra_stat || '',
      new_parent: isHighPriority ? getPriorityParentValue(item) : false,
      automatedprice: automatedpriceValue,
      automatedprice_sign: normalizedSign
    };

    const res = await fetch(getListEndpoint('edit'), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Pricing update failed');

    if (!isHighPriority) {
      await syncHighPriorityParent(
        {
          ...item,
          desired_price: desiredPrice,
          automatedprice: automatedpriceValue,
          automatedprice_sign: normalizedSign
        },
        {
          automatedprice: automatedpriceValue,
          automatedprice_sign: normalizedSign
        }
      );
    }
    return data;
  };

  const applyPricingUpdates = async (updates, doneLabel) => {
    if (!updates.length) {
      setPricingStatus('No matching prices to update');
      return;
    }

    setPricingStatus(`Updating ${updates.length} prices...`);
    try {
      let latest = null;
      for (const update of updates) {
        latest = await updateItemPricing(
          update.item,
          update.desired_price,
          update.automatedprice,
          update.automatedprice_sign || 'minus'
        );
      }
      if (latest) setItems(latest);
      setPricingStatus(doneLabel || `Updated ${updates.length} prices`);
    } catch (e) {
      setError(e.message || 'Pricing update failed');
      setPricingStatus('');
    }
  };

  const saveCurrentPrices = () => {
    const snapshot = {};
    items.forEach(item => {
      snapshot[getItemIdentity(item)] = {
        desired_price: item.desired_price,
        automatedprice: item.automatedprice ?? null,
        automatedprice_sign: getAutomatedPriceSign(item),
        has_automatedprice: item.automatedprice != null
      };
    });
    localStorage.setItem(
      priceSnapshotStorageKey,
      JSON.stringify({ saved_at: new Date().toISOString(), items: snapshot })
    );
    setPricingStatus(`Saved ${items.length} current prices`);
  };

  const restoreSavedPrices = async () => {
    let snapshot;
    try {
      snapshot = JSON.parse(localStorage.getItem(priceSnapshotStorageKey) || '{}');
    } catch {
      snapshot = {};
    }

    const savedItems = snapshot.items || {};
    const updates = items
      .map(item => {
        const saved = savedItems[getItemIdentity(item)];
        if (!saved) return null;
        return {
          item,
          desired_price: saved.desired_price,
          automatedprice: saved.has_automatedprice ? saved.automatedprice : false,
          automatedprice_sign: saved.has_automatedprice && saved.automatedprice_sign === 'plus'
            ? 'plus'
            : 'minus'
        };
      })
      .filter(Boolean);

    const skipped = items.length - updates.length;
    await applyPricingUpdates(
      updates,
      `Restored ${updates.length} prices${skipped ? `, left ${skipped} new items unchanged` : ''}`
    );
  };

  const applySelectedFormula = async () => {
    const updates = [];
    for (const item of automatedItems) {
      const identity = getItemIdentity(item);
      if (pricingExclusions[identity]) continue;
      const result = getPricingFormulaResult(item);
      if (!result) continue;
      updates.push({ item, ...result });
    }

    await applyPricingUpdates(updates, `Applied formula to ${updates.length} automated prices`);
  };

  const setAllPricingExclusions = excluded => {
    const next = {};
    automatedItems.forEach(item => {
      if (excluded) next[getItemIdentity(item)] = true;
    });
    setPricingExclusions(next);
  };

  const handleErrorOk = () => setError(null);

  // ─── Render ─────────────────────────────────────────────────
  return (
    <div className="container">
      <nav className="side-menu">
        <button
          className={activePage === 'items' && listMode === 'normal' ? 'nav-button active' : 'nav-button'}
          onClick={() => openList('normal')}
        >
          Normal List
        </button>
        <button
          className={activePage === 'items' && isHighPriority ? 'nav-button active' : 'nav-button'}
          onClick={() => openList('highPriority')}
        >
          High Priority
        </button>
        <button
          className={activePage === 'items' && isSpecialList ? 'nav-button nav-button-secondary active' : 'nav-button nav-button-secondary'}
          onClick={() => openList('specialList')}
        >
          Special List
        </button>
        {activePage === 'items' && !isSpecialList && (
          <button className="nav-button" onClick={openAdd}>
            Add Item
          </button>
        )}
        {activePage === 'items' && !isHighPriority && !isSpecialList && (
          <button className="nav-button" onClick={openImport}>
            Import List
          </button>
        )}
        {activePage === 'items' && !isSpecialList && (
          <button className="nav-button" onClick={() => setShowPricingModal(true)}>
            Pricing Formulas
          </button>
        )}
        {activePage === 'items' && (
          <button className="nav-button" onClick={toggleRegion}>
            Switch to {region === 'na' ? 'eu' : 'na'}
          </button>
        )}
        <button
          className={activePage === 'schedules' ? 'nav-button active' : 'nav-button'}
          onClick={() => setActivePage('schedules')}
        >
          Schedules
        </button>
        <button
          className={activePage === 'files' ? 'nav-button active' : 'nav-button'}
          onClick={() => setActivePage('files')}
        >
          PointBlank File
        </button>
        {activePage === 'items' && selectionMode && (
          <div className="bulk-status">
            {selectedCount} selected
          </div>
        )}
        {activePage === 'items' && bulkStatus && <div className="bulk-status">{bulkStatus}</div>}
      </nav>

      <main className="main-content">
        {activePage === 'schedules' ? (
          <SchedulesPanel />
        ) : activePage === 'files' ? (
          <PointBlankPanel />
        ) : (
          <table>
          <thead>
            <tr>
              {selectionMode && (
                <th className="selection-cell">
                  <input
                    type="checkbox"
                    checked={items.length > 0 && items.every(item => selectedItems[getItemIdentity(item)])}
                    onChange={e => {
                      if (e.target.checked) {
                        const next = {};
                        items.forEach(item => { next[getItemIdentity(item)] = true; });
                        setSelectedItems(next);
                      } else {
                        setSelectedItems({});
                      }
                    }}
                  />
                </th>
              )}
              <th>Name</th>
              {merged ? (
                <th onDoubleClick={() => setMerged(false)} className="sortable">
                  ID/Pet ID
                </th>
              ) : (
                <>
                  <th onDoubleClick={() => setMerged(true)} className="sortable">
                    ID
                  </th>
                  <th className="sortable">Pet ID</th>
                </>
              )}
              <th
                className="sortable"
                onDoubleClick={() => toggleSort('ilvl')}
              >
                iLvl{' '}
                {sortConfig.key === 'ilvl'
                  ? sortConfig.direction === 'desc'
                    ? '▲'
                    : '▼'
                  : ''}
              </th>
              {showsVariants && <th>Variant</th>}
              <th
                className="sortable"
                onDoubleClick={() => toggleSort('desired_price')}
              >
                Desired Price{' '}
                {sortConfig.key === 'desired_price'
                  ? sortConfig.direction === 'desc'
                    ? '▲'
                    : '▼'
                  : ''}
              </th>
              <th>N Avg</th>
              <th className="scrollable">10 Lowest</th>
              <th>Delete</th>
            </tr>
          </thead>
          <tbody>
            {displayedRows.map((row, ridx) => {
              if (row.type === 'variant-group') {
                const first = row.items[0];
                const groupSelected = row.items.length > 0 && row.items.every(
                  item => selectedItems[getItemIdentity(item)]
                );
                const groupSpecial = row.items.some(isSpecialAlertItem);
                const desiredPrices = [...new Set(row.items.map(item => Number(item.desired_price)))];
                const desiredLabel = desiredPrices.length === 1
                  ? desiredPrices[0].toLocaleString()
                  : `${Math.min(...desiredPrices).toLocaleString()} – ${Math.max(...desiredPrices).toLocaleString()}`;
                const averages = row.items
                  .map(item => Number(item.min_n_avg_gold))
                  .filter(Number.isFinite);
                const averageLabel = averages.length
                  ? (Math.min(...averages) === Math.max(...averages)
                      ? Math.min(...averages).toFixed(2)
                      : `${Math.min(...averages).toFixed(2)} – ${Math.max(...averages).toFixed(2)}`)
                  : '';
                const automationLabels = [...new Set(row.items
                  .filter(item => item.automatedprice != null)
                  .map(item => `${getAutomatedPriceSign(item) === 'plus' ? '+' : '-'}${Number(item.automatedprice).toFixed(0)}%`))];
                return (
                  <tr
                    key={row.key}
                    className={[
                      'variant-category-row',
                      row.isChild ? 'priority-child-row' : '',
                      row.isOrphan ? 'priority-orphan-row' : ''
                    ].filter(Boolean).join(' ')}
                    onContextMenu={e => handleContextMenu(e, null, row.key)}
                  >
                    {selectionMode && (
                      <td className="selection-cell">
                        <input
                          type="checkbox"
                          checked={groupSelected}
                          onChange={() => toggleSelectedGroup(row.items)}
                        />
                      </td>
                    )}
                    <td>
                      <span className={groupSpecial ? 'special-alert-name' : ''}>{first.name}</span>
                    </td>
                    {merged ? (
                      <td>{getKey(first)}</td>
                    ) : (
                      <>
                        <td>{first.id ?? ''}</td>
                        <td>{first.pet_species_id ?? ''}</td>
                      </>
                    )}
                    <td>{first.ilvl}</td>
                    <td>
                      <button
                        type="button"
                        className="variant-group-toggle"
                        title="Expand category"
                        onClick={() => toggleFold(row.key)}
                      >
                        ▶
                      </button>
                      <span className="variant-cell">{row.label}</span>
                      <span className="variant-count"> ({row.items.length})</span>
                    </td>
                    <td className={automationLabels.length === 1 ? 'desired-price-cell automated' : 'desired-price-cell'}>
                      {automationLabels.length === 1 && (
                        <div className="automated-percentage">{automationLabels[0]}</div>
                      )}
                      <div className="desired-price-value">{desiredLabel}</div>
                    </td>
                    <td>{averageLabel}</td>
                    <td className="scrollable"></td>
                    <td></td>
                  </tr>
                );
              }

              if (row.type === 'group') {
                const groupSelected = row.items.length > 0 && row.items.every(it => selectedItems[getItemIdentity(it)]);
                const groupSpecial = row.items.some(isSpecialAlertItem);
                return (
                  <tr key={`group-${row.key}`}>
                    {selectionMode && (
                      <td className="selection-cell">
                        <input
                          type="checkbox"
                          checked={groupSelected}
                          onChange={() => toggleSelectedGroup(row.items)}
                        />
                      </td>
                    )}
                    <td>
                      <span className={groupSpecial ? 'special-alert-name' : ''}>
                        {row.name}
                      </span>
                    </td>
                    {merged ? (
                      <td
                        onContextMenu={e =>
                          handleContextMenu(e, null, row.key)
                        }
                      >
                        {row.items.map((it, i) => (
                          <Fragment key={i}>
                            {getKey(it)}
                            {i < row.items.length - 1 && ' / '}
                          </Fragment>
                        ))}
                      </td>
                    ) : (
                      <>
                        <td
                          onContextMenu={e =>
                            handleContextMenu(e, null, row.key)
                          }
                        >
                          {row.items.map((it, i) => (
                            <Fragment key={i}>
                              {it.id ?? ''}
                              {i < row.items.length - 1 && ' / '}
                            </Fragment>
                          ))}
                        </td>
                        <td>
                          {row.items.map((it, i) => (
                            <Fragment key={i}>
                              {it.pet_species_id ?? ''}
                              {i < row.items.length - 1 && ' / '}
                            </Fragment>
                          ))}
                        </td>
                      </>
                    )}
                    <td>
                      {row.items.map((it, i) => (
                        <Fragment key={i}>
                          {it.ilvl}
                          {i < row.items.length - 1 && ' / '}
                        </Fragment>
                      ))}
                    </td>
                    {showsVariants && (
                      <td>
                        {row.items.map((it, i) => (
                          <Fragment key={i}>
                            {getVariantLabel(it)}
                            {i < row.items.length - 1 && ' / '}
                          </Fragment>
                        ))}
                      </td>
                    )}
                    <td>
                      {row.items.map((it, i) => (
                        <Fragment key={i}>
                          {it.desired_price.toLocaleString()}
                          {i < row.items.length - 1 && ' / '}
                        </Fragment>
                      ))}
                    </td>
                    <td></td>
                    <td className="scrollable"></td>
                    <td></td>
                  </tr>
                );
              }

              const item = row.item;
              const idx = items.findIndex(x => x === item);
              const isChildRow = Boolean(row.isChild);
              const isOrphanRow = Boolean(row.isOrphan);
              const isPending = pendingData.hasOwnProperty(idx);
              const isExpanded = expandedRows[idx] === true;
              const nAvg = item.min_n_avg_gold;
              const ten = item.ten_lowest || [];
              const preview = ten
                .slice(0, 3)
                .map(e => `${e.price_gold.toFixed(2)}g@${e.realm}`);
              const needsEllipsis = !isExpanded && ten.length > 3;
              const displayList = isExpanded
                ? ten.map(e => `${e.price_gold.toFixed(2)}g@${e.realm}`)
                : preview;
              const selected = Boolean(selectedItems[getItemIdentity(item)]);

              return (
                <tr
                  key={`${getKey(item)}-${item.ilvl}-${item.socket ? 'socket' : 'base'}-${item.extra_stat || 'none'}-${idx}`}
                  className={[
                    isChildRow ? 'priority-child-row' : '',
                    isOrphanRow ? 'priority-orphan-row' : '',
                    selected ? 'selected-row' : ''
                  ].filter(Boolean).join(' ')}
                  onContextMenu={e => handleContextMenu(e, idx, getVariantGroupKey(item) || getKey(item))}
                  onClick={e => handleSelectionRowClick(e, item)}
                >
                  {selectionMode && (
                    <td className="selection-cell">
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleSelectedItem(item)}
                      />
                    </td>
                  )}
                  <td
                    onDoubleClick={() =>
                      handleCellDoubleClick(idx, 'name')
                    }
                  >
                    {editingCell.rowIndex === idx &&
                    editingCell.key === 'name' ? (
                      <input
                        type="text"
                        defaultValue={item.name}
                        autoFocus
                        onBlur={e =>
                          commitChange(e.target.value, idx, 'name')
                        }
                        onKeyDown={e =>
                          e.key === 'Enter' && e.target.blur()
                        }
                      />
                    ) : (
                      <span className="name-cell">
                        {isPending && (
                          <button onClick={() => handleSave(idx)}>
                            💾
                          </button>
                        )}
                        <button
                          onClick={() =>
                            navigator.clipboard.writeText(item.name)
                          }
                          className="copy-button"
                          title="Copy name"
                        >
                          📋
                        </button>
                        <span className={isSpecialAlertItem(item) ? 'special-alert-name' : ''}>
                          {item.name}
                        </span>
                      </span>
                    )}
                  </td>

                  {merged ? (
                    <td
                      onDoubleClick={() =>
                        handleCellDoubleClick(idx, 'combined')
                      }
                      onContextMenu={e =>
                        handleContextMenu(e, idx, getKey(item))
                      }
                    >
                      {editingCell.rowIndex === idx &&
                      editingCell.key === 'combined' ? (
                        <CombinedEditor
                          rowIndex={idx}
                          item={item}
                          onCommit={commitCombined}
                        />
                      ) : (
                        <span>{getKey(item)}</span>
                      )}
                    </td>
                  ) : (
                    <>
                      <td
                        onDoubleClick={() =>
                          handleCellDoubleClick(idx, 'id')
                        }
                      >
                        {editingCell.rowIndex === idx &&
                        editingCell.key === 'id' ? (
                          <input
                            type="number"
                            inputMode="numeric"
                            defaultValue={item.id ?? ''}
                            autoFocus
                            onBlur={e =>
                              commitChange(e.target.value, idx, 'id')
                            }
                            onKeyDown={e =>
                              e.key === 'Enter' && e.target.blur()
                            }
                          />
                        ) : (
                          item.id ?? ''
                        )}
                      </td>
                      <td
                        onDoubleClick={() =>
                          handleCellDoubleClick(
                            idx,
                            'pet_species_id'
                          )
                        }
                      >
                        {editingCell.rowIndex === idx &&
                        editingCell.key === 'pet_species_id' ? (
                          <input
                            type="number"
                            inputMode="numeric"
                            defaultValue={item.pet_species_id ?? ''}
                            autoFocus
                            onBlur={e =>
                              commitChange(
                                e.target.value,
                                idx,
                                'pet_species_id'
                              )
                            }
                            onKeyDown={e =>
                              e.key === 'Enter' && e.target.blur()
                            }
                          />
                        ) : (
                          item.pet_species_id ?? ''
                        )}
                      </td>
                    </>
                  )}

                  <td
                    onDoubleClick={() =>
                      handleCellDoubleClick(idx, 'ilvl')
                    }
                  >
                    {editingCell.rowIndex === idx &&
                    editingCell.key === 'ilvl' ? (
                      <input
                        type="text"
                        defaultValue={item.ilvl}
                        autoFocus
                        onBlur={e =>
                          commitChange(e.target.value, idx, 'ilvl')
                        }
                        onKeyDown={e =>
                          e.key === 'Enter' && e.target.blur()
                        }
                      />
                    ) : (
                      item.ilvl
                    )}
                  </td>

                  {showsVariants && (
                    <td>
                      {getVariantGroupKey(item) && (
                        <button
                          type="button"
                          className="variant-group-toggle"
                          title="Collapse category"
                          onClick={() => toggleFold(getVariantGroupKey(item))}
                        >
                          ▼
                        </button>
                      )}
                      <span className="variant-cell">{getVariantLabel(item)}</span>
                    </td>
                  )}

                  <td
                      className={item.automatedprice
                        ? 'desired-price-cell automated'
                        : 'desired-price-cell'}
                      onDoubleClick={() => handleCellDoubleClick(idx, 'desired_price')}
                      onContextMenu={e => handlePriceContextMenu(e, idx)}
                    >
                      {editingCell.rowIndex === idx && editingCell.key === 'desired_price' ? (
                        <input
                          type="text"
                          defaultValue={item.desired_price}
                          autoFocus
                          onBlur={e => commitChange(e.target.value, idx, 'desired_price')}
                          onKeyDown={e => e.key === 'Enter' && e.target.blur()}
                        />
                      ) : (
                        <>
                          {item.automatedprice != null && (
                            <div className="automated-percentage">
                              {getAutomatedPriceSign(item) === 'plus' ? '+' : '-'}
                              {item.automatedprice.toFixed(0)}%
                            </div>
                          )}
                          <div className="desired-price-value">
                            {item.desired_price.toLocaleString()}
                          </div>
                        </>
                      )}
                  </td>

                  <td>{nAvg != null ? nAvg.toFixed(2) : ''}</td>

                  <td className="scrollable">
                    <span className="lowest-cell">
                      <span>
                        {displayList.join(', ')}
                        {needsEllipsis ? '…' : ''}
                      </span>
                      {ten.length > 3 && (
                        <button
                          onClick={() => toggleExpand(idx)}
                          className="expand-button"
                          title={
                            expandedRows[idx]
                              ? 'Show less'
                              : 'Show more'
                          }
                        >
                          {expandedRows[idx] ? '▲' : '▼'}
                        </button>
                      )}
                    </span>
                  </td>

                  <td>
                    <button
                      className="delete-button"
                      onClick={() => handleDelete(item)}
                    >
                      {isSpecialList ? 'Remove' : 'Delete'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          </table>
        )}
      </main>

      {/* Add‐Item Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={closeAdd}>
          <div
            className="modal-content"
            onClick={e => e.stopPropagation()}
          >
            <h2>{isHighPriority ? 'Add High Priority Item' : 'Add New Item'}</h2>
            <input
              type="text"
              placeholder="Name"
              value={newItem.name}
              onChange={e =>
                setNewItem(n => ({ ...n, name: e.target.value }))
              }
            />
            <div className="combined-editor">
              <input
                type="number"
                inputMode="numeric"
                placeholder="ID"
                disabled={newItem.pet_species_id.trim() !== ''}
                value={newItem.id}
                onChange={e =>
                  setNewItem(n => ({ ...n, id: e.target.value }))
                }
              />
              <span>/</span>
              <input
                type="number"
                inputMode="numeric"
                placeholder="Pet ID"
                disabled={newItem.id.trim() !== ''}
                value={newItem.pet_species_id}
                onChange={e =>
                  setNewItem(n => ({
                    ...n,
                    pet_species_id: e.target.value
                  }))
                }
              />
            </div>
            <input
              type="number"
              placeholder="iLvl"
              value={newItem.ilvl}
              onChange={e =>
                setNewItem(n => ({ ...n, ilvl: e.target.value }))
              }
            />
            <input
              type="number"
              placeholder="Desired Price"
              value={newItem.desired_price}
              onChange={e =>
                setNewItem(n => ({
                  ...n,
                  desired_price: e.target.value
                }))
              }
            />
            {isHighPriority && (
              <div className="variant-editor">
                <label>
                  <input
                    type="checkbox"
                    checked={newItem.socket}
                    onChange={e =>
                      setNewItem(n => ({ ...n, socket: e.target.checked }))
                    }
                  />
                  Socket
                </label>
                <select
                  value={newItem.extra_stat}
                  onChange={e =>
                    setNewItem(n => ({ ...n, extra_stat: e.target.value }))
                  }
                >
                  {EXTRA_STATS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <label className="cross-region-option">
              <input
                type="checkbox"
                checked={newItem.add_other_region}
                onChange={e =>
                  setNewItem(n => ({ ...n, add_other_region: e.target.checked }))
                }
              />
              Add to {region === 'na' ? 'EU' : 'NA'} as well
            </label>
            <div className="modal-buttons">
              <button onClick={closeAdd}>Cancel</button>
              <button onClick={confirmAdd}>Add</button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="modal-overlay" onClick={closeImport}>
          <div
            className="modal-content"
            onClick={e => e.stopPropagation()}
          >
            <h2>Import Shopping List</h2>
            <textarea
              value={importInput}
              onChange={e => setImportInput(e.target.value)}
              placeholder="Paste your list here…"
            />
            <div className="modal-buttons">
              <button onClick={closeImport}>Cancel</button>
              <button onClick={confirmImport}>Import</button>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Formula Modal */}
      {showPricingModal && (
        <div className="modal-overlay" onClick={() => setShowPricingModal(false)}>
          <div
            className="modal-content pricing-modal"
            onClick={e => e.stopPropagation()}
          >
            <h2>Pricing Formulas</h2>
            <select
              value={pricingFormula}
              onChange={e => setPricingFormula(e.target.value)}
            >
              {PRICING_FORMULAS.map(formula => (
                <option key={formula.value} value={formula.value}>
                  {formula.label} - {formula.detail}
                </option>
              ))}
            </select>
            {PROFIT_TARGET_FORMULAS.has(pricingFormula) && (
              <div className="pricing-target-row">
                <input
                  type="number"
                  min="0"
                  value={profitTarget}
                  onChange={e => setProfitTarget(e.target.value)}
                  placeholder="Base profit target"
                />
                <span>
                  {pricingFormula === 'fixed_profit'
                    ? 'Same profit target for every automated item'
                    : pricingFormula === 'profit_band'
                      ? 'Used as the 300k+ target; lower items use 0.67x, 1m+ uses 1.33x'
                      : 'Used as the low-item target; 300k+ uses 1.8x, 1m+ uses 3x, 2m+ uses 5x'}
                </span>
              </div>
            )}
            <div className="pricing-actions">
              <button onClick={saveCurrentPrices}>Save current prices</button>
              <button onClick={restoreSavedPrices}>Restore saved prices</button>
              <button onClick={() => setAllPricingExclusions(false)}>Include all</button>
              <button onClick={() => setAllPricingExclusions(true)}>Exclude all</button>
            </div>
            <div className="pricing-list">
              {automatedItems.length === 0 ? (
                <p>No automated prices in this view.</p>
              ) : automatedItems.map(item => {
                const identity = getItemIdentity(item);
                const result = getPricingFormulaResult(item);
                const excluded = Boolean(pricingExclusions[identity]);
                return (
                  <label key={identity} className="pricing-row">
                    <input
                      type="checkbox"
                      checked={!excluded}
                      onChange={e =>
                        setPricingExclusions(prev => {
                          const next = { ...prev };
                          if (e.target.checked) delete next[identity];
                          else next[identity] = true;
                          return next;
                        })
                      }
                    />
                    <span className="pricing-row-name">
                      {item.name} ({getKey(item)}, ilvl {item.ilvl})
                    </span>
                    <span className="pricing-row-preview">
                      {result
                        ? `${formatGold(getAutomationAverage(item))} -> ${formatGold(result.desired_price)} (-${result.automatedprice}%, profit ${formatGold(getFormulaProfitTarget(Number(getAutomationAverage(item))))})`
                        : 'No N Avg'}
                    </span>
                  </label>
                );
              })}
            </div>
            {pricingStatus && <p className="pricing-status">{pricingStatus}</p>}
            <div className="modal-buttons">
              <button onClick={() => setShowPricingModal(false)}>Close</button>
              <button onClick={applySelectedFormula}>Apply formula</button>
            </div>
          </div>
        </div>
      )}

      {/* Variant Modal */}
      {ilvlVariationModal.visible && (
        <div className="modal-overlay" onClick={closeIlvlVariationModal}>
          <div className="modal-content ilvl-variation-modal" onClick={e => e.stopPropagation()}>
            <h2>Add iLvl Variation</h2>
            <p>
              {ilvlVariationModal.items.length === 1
                ? ilvlVariationModal.items[0].name
                : `${ilvlVariationModal.items.length} selected items`}
            </p>
            <input
              type="text"
              inputMode="numeric"
              autoFocus
              placeholder="305 or 305, 308, 309"
              value={ilvlVariationModal.ilvls}
              onChange={e => setIlvlVariationModal(current => ({
                ...current,
                ilvls: e.target.value
              }))}
              onKeyDown={e => e.key === 'Enter' && confirmIlvlVariations()}
            />
            <div className="ilvl-variation-options">
              <label>
                <input
                  type="radio"
                  name="ilvl-desired-price"
                  value="one_gold"
                  checked={ilvlVariationModal.desired_price_mode === 'one_gold'}
                  onChange={() => setIlvlVariationModal(current => ({
                    ...current,
                    desired_price_mode: 'one_gold'
                  }))}
                />
                Set desired price to 1 gold (default)
              </label>
              <label>
                <input
                  type="radio"
                  name="ilvl-desired-price"
                  value="zero"
                  checked={ilvlVariationModal.desired_price_mode === 'zero'}
                  onChange={() => setIlvlVariationModal(current => ({
                    ...current,
                    desired_price_mode: 'zero'
                  }))}
                />
                Set desired price to 0
              </label>
              <label>
                <input
                  type="radio"
                  name="ilvl-desired-price"
                  value="copy"
                  checked={ilvlVariationModal.desired_price_mode === 'copy'}
                  onChange={() => setIlvlVariationModal(current => ({
                    ...current,
                    desired_price_mode: 'copy'
                  }))}
                />
                Copy desired price from original item
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={ilvlVariationModal.add_other_region}
                  onChange={e => setIlvlVariationModal(current => ({
                    ...current,
                    add_other_region: e.target.checked
                  }))}
                />
                Do this to {region === 'na' ? 'EU' : 'NA'} items as well
              </label>
            </div>
            <div className="modal-buttons">
              <button disabled={ilvlVariationModal.submitting} onClick={closeIlvlVariationModal}>
                Cancel
              </button>
              <button disabled={ilvlVariationModal.submitting} onClick={confirmIlvlVariations}>
                {ilvlVariationModal.submitting ? 'Adding...' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {variantModal.visible && (
        <div
          className="modal-overlay"
          onClick={() => setVariantModal({ ...EMPTY_VARIANT_MODAL })}
        >
          <div
            className="modal-content"
            onClick={e => e.stopPropagation()}
          >
            <h2>Add High Priority Variant</h2>
            <p>
              {variantModal.items.length === 1
                ? variantModal.items[0].name
                : `${variantModal.items.length} selected items`}
            </p>
            <div className="variant-editor">
              <label>
                <input
                  type="checkbox"
                  checked={variantModal.socket}
                  disabled={variantModal.extra_stat === 'any_or_socket'}
                  onChange={e =>
                    setVariantModal(v => ({ ...v, socket: e.target.checked }))
                  }
                />
                Socket
              </label>
              <select
                value={variantModal.extra_stat}
                onChange={e => {
                  const extra_stat = e.target.value;
                  setVariantModal(v => ({
                    ...v,
                    extra_stat,
                    socket: extra_stat === 'any_or_socket' ? false : v.socket
                  }));
                }}
              >
                {EXTRA_STATS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="variant-automation-editor">
              <span>Automated price % (optional)</span>
              <div className="automation-sign-control" aria-label="Automated price sign">
                <button
                  type="button"
                  className={variantModal.automatedprice_sign === 'minus' ? 'active' : ''}
                  aria-pressed={variantModal.automatedprice_sign === 'minus'}
                  onClick={() => setVariantModal(v => ({ ...v, automatedprice_sign: 'minus' }))}
                >
                  −
                </button>
                <button
                  type="button"
                  className={variantModal.automatedprice_sign === 'plus' ? 'active' : ''}
                  aria-pressed={variantModal.automatedprice_sign === 'plus'}
                  onClick={() => setVariantModal(v => ({ ...v, automatedprice_sign: 'plus' }))}
                >
                  +
                </button>
              </div>
              <input
                type="number"
                min="0"
                max={variantModal.automatedprice_sign === 'minus' ? '100' : undefined}
                placeholder="None"
                value={variantModal.automatedprice}
                onChange={e => {
                  const value = e.target.value;
                  setVariantModal(v => ({
                    ...v,
                    automatedprice: value === ''
                      ? ''
                      : String(normalizeAutomatedPercentage(value, v.automatedprice_sign))
                  }));
                }}
              />
            </div>
            <div className="modal-buttons">
              <button onClick={() => setVariantModal({ ...EMPTY_VARIANT_MODAL })}>
                Cancel
              </button>
              <button onClick={confirmVariant}>Add</button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {error && (
        <div
          className="modal-overlay"
          onClick={handleErrorOk}
        >
          <div
            className="modal-content"
            onClick={e => e.stopPropagation()}
          >
            <h2>Error</h2>
            <p>{error}</p>
            <div className="modal-buttons">
              <button onClick={handleErrorOk}>OK</button>
            </div>
          </div>
        </div>
      )}

      {/* Context Menu */}
      {ctxMenu.visible && (() => {
        const item = ctxMenu.rowIndex != null ? items[ctxMenu.rowIndex] : null;
        const foldKey = ctxMenu.groupKey ?? (item ? getKey(item) : null);
        const canFold = foldKey != null && (!showsVariants || isVariantGroupKey(foldKey));
        const actionItems = getContextActionItems(item, ctxMenu.groupKey);
        const actionCount = actionItems.length;
        const compressedGroupItems = isHighPriority && item == null && isVariantGroupKey(ctxMenu.groupKey)
          ? items.filter(target => getVariantGroupKey(target) === ctxMenu.groupKey)
          : [];
        return (
          <div
            ref={ctxMenuRef}
            className="context-menu"
            style={{ top: ctxMenu.y, left: ctxMenu.x }}
          >
            <ul>
              <li onClick={() => {
                if (selectionMode) {
                  setSelectionMode(false);
                  setSelectedItems({});
                } else {
                  setSelectionMode(true);
                  const initialTargets = getContextActionItems(item, ctxMenu.groupKey);
                  if (initialTargets.length) {
                    const next = {};
                    initialTargets.forEach(target => { next[getItemIdentity(target)] = true; });
                    setSelectedItems(next);
                  }
                }
                setCtxMenu({ visible: false, x: 0, y: 0, rowIndex: null, groupKey: null });
              }}>
                {selectionMode ? 'Exit selection' : 'Select'}
              </li>
              {canFold && (
                <li onClick={() => {
                  toggleFold(foldKey);
                  setCtxMenu({ visible: false, x: 0, y: 0, rowIndex: null, groupKey: null });
                }}>
                  {isFolded(foldKey) ? 'Unfold' : 'Fold'}
                </li>
              )}
              {compressedGroupItems.length > 0 && (
                <li onClick={() => {
                  setCtxMenu({ visible: false, x: 0, y: 0, rowIndex: null, groupKey: null });
                  openAutomatedModal(compressedGroupItems);
                }}>
                  Change automated price for all children
                </li>
              )}
              {actionCount > 0 && !isHighPriority && !isSpecialList && (
                <li onClick={async () => {
                  setCtxMenu({ visible: false, x: 0, y: 0, rowIndex: null, groupKey: null });
                  await addManyToHighPriority(actionItems);
                }}>
                  Add into high priority list{actionCount > 1 ? ` (${actionCount})` : ''}
                </li>
              )}
              {actionCount > 0 && isHighPriority && (
                <li onClick={async () => {
                  setCtxMenu({ visible: false, x: 0, y: 0, rowIndex: null, groupKey: null });
                  await deleteManyHighPriority(actionItems);
                }}>
                  Remove from high priority list{actionCount > 1 ? ` (${actionCount})` : ''}
                </li>
              )}
              {actionCount > 0 && !isSpecialList && (
                <li onClick={() => {
                  setCtxMenu({ visible: false, x: 0, y: 0, rowIndex: null, groupKey: null });
                  openIlvlVariationModal(actionItems);
                }}>
                  Add iLvl variation{actionCount > 1 ? ` (${actionCount})` : ''}
                </li>
              )}
              {actionCount > 0 && !isSpecialList && actionItems.some(target => Number(target.ilvl) !== 0) && (
                <li onClick={async () => {
                  setCtxMenu({ visible: false, x: 0, y: 0, rowIndex: null, groupKey: null });
                  openVariantModal(actionItems);
                }}>
                  Add socket/extra stat variety{actionCount > 1 ? ` (${actionCount})` : ''}
                </li>
              )}
              {actionCount > 0 && actionItems.some(target => !isSpecialAlertItem(target)) && !isSpecialList && (
                <li onClick={async () => {
                  setCtxMenu({ visible: false, x: 0, y: 0, rowIndex: null, groupKey: null });
                  await addManyToSpecialList(actionItems);
                }}>
                  Add into special Telegram list{actionCount > 1 ? ` (${actionCount})` : ''}
                </li>
              )}
              {actionCount > 0 && actionItems.some(isSpecialAlertItem) && (
                <li onClick={async () => {
                  setCtxMenu({ visible: false, x: 0, y: 0, rowIndex: null, groupKey: null });
                  await removeManyFromSpecialList(actionItems);
                }}>
                  Remove from special Telegram list{actionCount > 1 ? ` (${actionCount})` : ''}
                </li>
              )}
            </ul>
          </div>
        );
      })()}

      {/* Price Context Menu */}
      {/* Price Context Menu */}
    {priceMenu.visible && (() => {
      const idx  = priceMenu.rowIndex;
      const item = items[idx];
      return (
        <div ref={priceMenuRef} className="context-menu" style={{ top: priceMenu.y, left: priceMenu.x }}>
          <ul>
            <li onClick={() => {
              setPriceMenu(pm => ({ ...pm, visible: false }));
              openAutomatedModal(item);
            }}>
              {item.automatedprice != null
                ? 'Change automated price %'
                : 'Set automated price'}
            </li>
            {item.automatedprice != null && (
              <li onClick={() => {
                setPriceMenu(pm => ({ ...pm, visible: false }));
                cancelAutomatedPrice(idx);
              }}>
                Cancel price automation
              </li>
            )}
          </ul>
        </div>
      );
    })()}


      {/* Automated Price Modal */}
      {autoModal.visible && (() => {
        const targetItems = autoModal.items.length
          ? autoModal.items
          : autoModal.rowIndex != null
            ? [items[autoModal.rowIndex]]
            : [];
        const item = targetItems[0];
        if (!item) return null;
        const isGroupAutomation = targetItems.length > 1;
        const nAvg       = getAutomationAverage(item);
        const usesParentAverage = isPriorityChild(item);

        // Parse the raw pct string into a number for calculations
        const automatedSign = autoModal.sign === 'plus' ? 'plus' : 'minus';
        const pctRaw     = autoModal.pct;
        const pctNum     = normalizeAutomatedPercentage(pctRaw, automatedSign);
        const priceMultiplier = automatedSign === 'plus'
          ? 1 + pctNum / 100
          : 1 - pctNum / 100;
        const newDesired = nAvg != null
          ? Math.round(nAvg * priceMultiplier)
          : '';

        return (
          <div
            className="modal-overlay"
            onClick={() => {
              if (!autoModal.submitting) setAutoModal({ ...EMPTY_AUTO_MODAL });
            }}
          >
            <div
              className="modal-content"
              onClick={e => e.stopPropagation()}
            >
              <h2>{isGroupAutomation ? 'Set Automated Price for All Children' : 'Set Automated Price'}</h2>
              {isGroupAutomation && <p>{targetItems.length} variants will be updated.</p>}

              {nAvg != null ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '1rem'
                  }}
                >
                  <span>
                    {usesParentAverage ? 'Base N Avg: ' : ''}
                    {nAvg.toLocaleString()}
                  </span>
                  <div className="automation-sign-control" aria-label="Automated price sign">
                    <button
                      type="button"
                      className={automatedSign === 'minus' ? 'active' : ''}
                      aria-pressed={automatedSign === 'minus'}
                      title="Subtract percentage"
                      onClick={() => setAutoModal(am => ({
                        ...am,
                        sign: 'minus',
                        pct: am.pct === ''
                          ? ''
                          : String(normalizeAutomatedPercentage(am.pct, 'minus'))
                      }))}
                    >
                      -
                    </button>
                    <button
                      type="button"
                      className={automatedSign === 'plus' ? 'active' : ''}
                      aria-pressed={automatedSign === 'plus'}
                      title="Add percentage"
                      onClick={() => setAutoModal(am => ({ ...am, sign: 'plus' }))}
                    >
                      +
                    </button>
                  </div>
                  <input
                    type="number"
                    min="0"
                    max={automatedSign === 'minus' ? 100 : undefined}
                    value={pctRaw}
                    onChange={e => {
                      const raw = e.target.value;
                      if (raw === '') {
                        // allow user to clear the field
                        setAutoModal(am => ({ ...am, pct: '' }));
                      } else {
                        let num = Number(raw);
                        if (isNaN(num)) num = 0;
                        num = normalizeAutomatedPercentage(num, automatedSign);
                        setAutoModal(am => ({ ...am, pct: String(num) }));
                      }
                    }}
                    style={{ width: '4rem' }}
                  />
                  <span>%</span>
                  <span>=</span>
                  <span>{newDesired.toLocaleString()}</span>
                </div>
              ) : (
                <p>
                  {usesParentAverage
                    ? 'No base N Avg available. Automated price will be set when the parent has an average.'
                    : 'No N Avg available. Automated price will be set on next cycle.'}
                </p>
              )}

              <div className="modal-buttons">
                <button
                  disabled={autoModal.submitting}
                  onClick={() => setAutoModal({ ...EMPTY_AUTO_MODAL })}
                >
                  Cancel
                </button>
                <button disabled={autoModal.submitting} onClick={confirmAutomated}>
                  {autoModal.submitting ? 'Updating...' : 'OK'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}


    </div>
  );
}

export default App;
